import React, { useState, useMemo, useCallback, memo, useRef, useLayoutEffect, useEffect, useContext, useId } from 'react';
import { List } from 'react-window';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Grid,
  Divider,
  Card,
  CardContent,
  useTheme,
  TableSortLabel,
  Skeleton,
  Autocomplete,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Toolbar,
  Tooltip,
  Popover

} from '@mui/material';

import {
  Edit as EditIcon,
  Warning as WarningIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Place as PlaceIcon,
  LocalOffer as CategoryIcon,
  Event as EventIcon,
  PictureAsPdf as PdfIcon,
  ConstructionOutlined,
  Delete as DeleteIcon,
  AccountBalanceWallet as SourceIcon,
  CompareArrows as ReviewMatchIcon,
  NorthEast as TransferOutIcon,
  SouthWest as TransferInIcon
} from '@mui/icons-material';
import { useGetEnrichedTransactionsByIDList, useGetEnrichedTransactions, useUpdateCategoryForTransactions, useUpdateMerchantForTransactions, useUpdateFlagForTransactions, useRestoreFlagForTransactions, useUndoRestoreFlagForTransactions, deriveIsRestoredFlag, useUpdateNotesForTransactions, useUpdateDescriptionForTransaction } from '../../api/transactions.js';
import { useTransferReviewActions } from '../../api/transfer_review.js';
import { deriveCardholderName, deriveCardEnding } from '../../utils/cardholder.js';
import { flagChipColor } from '../../utils/flagWithheld.js';
import CardholderCell from './CardholderCell.js';
import { useConfirmAction } from './confirmactionhandler.js';
import SnackbarContext from '../../context/snackbar-context.js';
import { Badge } from '../../design-system';
import { SortLevelsEditor, SortLevelsSummary } from './SortLevelsPanel.js';
import {
  addSortLevel,
  availableSortFields,
  DEFAULT_SORT_KEYS,
  describeSortKeys,
  isBlankSortKey,
  isDefaultSortKeys,
  nextSortKeys,
  SORT_FIELD_LABELS,
  SORTABLE_FIELDS,
  sortKeysSignature,
} from './transactionSortKeys.js';

// import { MerchantLocationMap, TripRouteMap } from './LocationMapComponents.js';
import TransactionDetailDialog from './TransactionDetailDialog.js';
import DataFilter from './filter/filtercontext.js';
import DateRangeFilter from './filter/filterdaterange.js';
import MultiSelectFilter from './filter/filtermultiselect.js';
import TextSearchFilter from './filter/filtertextsearch.js';
import NumericRangeFilter from './filter/filternumericrange.js';
import { formatDate, formatCurrency, formatInteger, formatPercentage, formatEntityName } from '../../utils/formatters.js'
import { perfLog, perfNow } from '../../utils/perfLog.js';
import { originalFilenameFromKey } from '../../utils/documentName.js'
import CategoryEditDialog from './CategoryEditDialog.js';
import MerchantEditDialog from './MerchantEditDialog.js';
import FlagEditDialog from './FlagEditDialog.js';
import SourceEditDialog from './SourceEditDialog.js';
import NotesEditDialog from './NotesEditDialog.js';
import DescriptionEditDialog from './DescriptionEditDialog.js';
import DescriptionCell from './DescriptionCell.js';
import TransferMatchDialog from './TransferMatchDialog.js';
// import { is } from 'date-fns/locale';
// import { set } from 'date-fns';
// import { use } from 'cytoscape';
import UserProfileContext from '../../context/userprofilecontext.js';


// Mapping function for transaction list items
const mapTransactionToListItem = (transaction, mapTransferPartner = true) => {
  // console.log("Mapping transaction to list item, mapTransferPartner:", mapTransferPartner, ", input:");
  // console.log(JSON.stringify(transaction, null, 2));

  const result = {
    bankAccountName: transaction.bankAccount?._displayName || 'Unknown Account',
    transactionDate: transaction.transaction?.posting_date || 'Unknown Date',
    description: transaction.transaction?.description || 'No Description',
    // User correction of the scanned description (set only while edited).
    descriptionEdited: transaction.transaction?.description_edited != null,
    descriptionOriginal: transaction.transaction?.description_original ?? null,
    descriptionEditedBy: transaction.transaction?.description_editedBy || null,
    descriptionEditedAt: transaction.transaction?.description_editedAt || null,
    amount: transaction.transaction?.amount || 0,
    merchantName: transaction.merchant?.name,
    merchantLocation: transaction.merchantLocation || 'Unknown Location',
    associatedNames: transaction.associatedNames?.map(name => name.name) || [],
    tripLocations: transaction.tripLocations?.map(location => location.name) || [],
    subcategoryName: transaction.subcategory?.name || 'Uncategorized',
    categoryName: transaction.category?.name || 'Uncategorized',
    categoryType: transaction.category?.type || 'Expense',
    flagName: transaction.flag?.name,
    notes: transaction.transaction?.notes || '',
    notesUpdatedBy: transaction.transaction?.notesUpdatedBy || null,
    // Whose card (server card_view); see CardholderCell.
    cardholderName: deriveCardholderName(transaction.card_view),
    cardEnding: deriveCardEnding(transaction.card_view),
    isCardRow: transaction.card_view?.scope === 'CARD',
    // Human-verdict state of the transfer link (surfaced on transfer_partner by
    // the enriched read). Drives the disposition badge + confirm/reject controls.
    transferDisposition: transaction.transfer_partner?.disposition || null,
    transferManual: transaction.transfer_partner?.manual || false,
    // Whether this is a transfer-categorized transaction — so an UNPAIRED one
    // still gets a "Review" entry point (to match it, or restore a rejected pair).
    isTransferCategorized:
      transaction.subcategory?.type === 'Transfer' ||
      ['Transfer In', 'Transfer Out'].includes(transaction.category?.name),
  };
  if (mapTransferPartner && transaction.hasTransferPartner) {
    result.transferPartner = mapTransactionToListItem(transaction.transfer_partner, false);
  }
  // console.log("Mapped list item result:");
  // console.log(JSON.stringify(result, null, 2));
  return result;
};

// Pick the first geocode that actually carries a Google place_id. The :Merchant
// node can hold a `failed_v1` sentinel (truthy but no place_id — e.g. a check
// payee like "A-Derma Inc" with no city, which won't geocode); in that case fall
// back to the :Location (MERCHANT_LOCATION) geocode, which for a check carries the
// precise payee address resolved by enrichment. Returns null when none are usable.
const pickGeocodeWithPlaceId = (...geocodes) => {
  for (const g of geocodes) {
    if (!g) continue;
    try {
      const parsed = typeof g === 'string' ? JSON.parse(g) : g;
      if (parsed && parsed.place_id) return g;
    } catch (e) {
      // malformed geocode JSON — skip
    }
  }
  return null;
};

// Mapping function for transaction details dialog
const mapTransactionToDetailItem = (transaction) => {

  const objectKey = transaction.transaction?._awsUploadedDocumentKey || transaction.transaction?._awsObjectKey || null;

  const detailItem = {
    // Identity (used to fetch the check-image crop, etc.)
    id: transaction.transaction?.id || null,
    // Basic Information
    bankAccountName: transaction.bankAccount?._displayName || 'Unknown Account',
    accountHolder: transaction.bankAccount?.account_holder || 'Unknown Holder',
    bankName: transaction.bankAccount?.bank_name || 'Unknown Bank',
    accountType: transaction.bankAccount?.account_type || 'Unknown Type',

    // Transaction Details
    transactionDate: transaction.transaction?.posting_date || 'Unknown Date',
    description: transaction.transaction?.description || 'No Description',
    descriptionEdited: transaction.transaction?.description_edited != null,
    descriptionOriginal: transaction.transaction?.description_original ?? null,
    descriptionEditedBy: transaction.transaction?.description_editedBy || null,
    descriptionEditedAt: transaction.transaction?.description_editedAt || null,
    amount: transaction.transaction?.amount || 0,
    bucketName: transaction.transaction?._awsBucketName || null,
    objectKey: objectKey,  //DEPRECATED: _awsObjectKey is being replaced by _awsUploadedDocumentKey, but we will check for both for now to maintain compatibility with existing transactions that may not have _awsUploadedDocumentKey populated yet
    // Original upload filename recovered from the `uploads/{uuid}_{name}` key —
    // drives the PDF-preview dialog title and the download-prompt filename.
    documentName: originalFilenameFromKey(objectKey),
    bbox: transaction.transaction?.bbox || null,
    page_number: transaction.transaction?.page_number || null,

    // Merchant Information
    merchantID: transaction.merchant?.id || null,
    merchantName: transaction.merchant?.name,
    merchantLocation: transaction.merchantLocation || 'Unknown Location',
    merchantWebsite: transaction.merchant?.website || null,
    merchantPhone: transaction.merchant?.phone || null,
    merchantStreetAddress: transaction.merchant?.street_address || null,
    merchantCity: transaction.merchant?.city || null,
    merchantState: transaction.merchant?.state || null,
    merchantZip: transaction.merchant?.zip || null,
    merchantGeocode: pickGeocodeWithPlaceId(transaction.merchant?.geocode, transaction.merchantLocationGeocode),

    // Check Details (populated when this transaction was read off a check image)
    checkNumber: transaction.transaction?.check_number || null,
    checkPayee: transaction.transaction?.check_payee || null,
    checkPayeeAddress: transaction.transaction?.check_payee_address || null,
    checkMemo: transaction.transaction?.check_memo || null,
    checkImagePage: transaction.transaction?.check_image_page || null,
    // doc_kind ("issued_check" | "deposit_slip") drives the card title + labels;
    // a stamped check_image_page means a native-resolution crop exists to view.
    docKind: transaction.transaction?.doc_kind || null,
    hasCheckImage: transaction.transaction?.check_image_page != null,

    // Travel Information
    tripOrigin: transaction.tripOrigin?.name || null,
    tripOriginGeocode: transaction.tripOrigin?.geocode || null,
    tripDestination: transaction.tripDestination?.name || null,
    tripDestinationGeocode: transaction.tripDestination?.geocode || null,
    tripLocations: transaction.tripLocations || [],
    tripStartDate: transaction.transaction?._trip_start || null,
    tripEndDate: transaction.transaction?._trip_end || null,

    // Associated People
    associatedNames: transaction.associatedNames || [],

    // Categorization
    subcategoryName: transaction.subcategory?.name || 'Uncategorized',
    categoryName: transaction.category?.name || 'Uncategorized',
    categoryRationale: transaction.category_rationale?.matched_basis || 'No rationale provided',

    // Flagging
    flagName: transaction.flag?.name || 'No Flag',
    flagRationale: transaction.flag_rationale?.matched_basis || 'No rationale provided',
    confidenceLevel: transaction.flag_rationale?.confidence_level || 'Unknown',
    // The flag's withheld state (t_1a266924): the dialog's chip colour
    // resolves through flagChipColor (utils/flagWithheld.js) — the SAME
    // rule the row chip uses — and that rule reads these two flat fields.
    // They ride the row object (P1-5 row contract, api/transactions.js);
    // assigned explicitly here because this mapper is a deliberate whitelist.
    flag_status: transaction.flag_status || null,
    flag_restored: transaction.flag_restored || false,

    // AI Analysis
    aiOutput: transaction.aiOutput || null,

    // Helper methods to check if location data is available
    hasMerchantMapData: function () {
      return !!this.merchantGeocode;
    },

    hasTripMapData: function () {
      return !!(this.tripOriginGeocode || this.tripDestinationGeocode);
    },

    hasMerchantInfo: function () {
      return (
        (this.merchantName) ||
        (this.merchantLocation && this.merchantLocation !== 'Unknown Location') ||
        this.merchantWebsite ||
        this.merchantPhone ||
        this.merchantStreetAddress ||
        this.merchantCity ||
        this.merchantState ||
        this.merchantZip
      );
    },

    hasCheckInfo: function () {
      return !!(
        this.checkNumber ||
        this.checkPayee ||
        this.checkPayeeAddress ||
        this.checkMemo
      );
    },

    // Function to determine if we have trip dates to display
    hasTripDates: function () {
      return this.tripStartDate || this.tripEndDate;
    },

    // Function to get appropriate label for trip dates section
    getTripDateLabel: function () {
      if (this.tripStartDate && this.tripEndDate) {
        return 'Travel Period';
      }
      return 'Date of Travel';
    },
  };

  return detailItem;
};

// Sorting utility functions
//
// Takes an ALREADY-MAPPED list item (see mapTransactionToListItem), not a raw
// transaction. Mapping is hoisted out to the caller so a sort maps each row once
// instead of once per operand per comparison — see sortTransactions below.
//
// Returns a genuinely EMPTY key for a missing value rather than a substitute
// that happens to sort where we want. A substitute can only be right in one
// direction: the old `'zzz'` flag sentinel pushed unflagged rows last
// ascending and pulled them first descending, which is not what a spreadsheet
// does and not what an accountant expects.
export const getComparableValue = (item, field) => {
  switch (field) {
    case 'transactionDate': {
      if (!item.transactionDate || item.transactionDate === 'Unknown Date') return null;
      const [year, month, day] = item.transactionDate.split('-').map(num => parseInt(num, 10));
      const time = new Date(year, month - 1, day).getTime();
      // An unparseable date is a blank, not the epoch.
      return Number.isNaN(time) ? null : time;
    }

    case 'amount':
      // 0 is a legitimate amount, never a blank.
      return item.amount || 0;

    // These three are coalesced upstream to 'Unknown Account' / 'No Description'
    // / 'Uncategorized', so they arrive as real strings the user can see and
    // sort — deliberately not treated as blanks.
    case 'bankAccountName':
      return (item.bankAccountName || '').toLowerCase();

    case 'description':
      return (item.description || '').toLowerCase();

    case 'subcategoryName':
      return (item.subcategoryName || '').toLowerCase();

    case 'cardholderName':
      return (item.cardholderName || '').toLowerCase();

    case 'merchantName':
      return (item.merchantName || '').toLowerCase();

    case 'flagName':
      return (item.flagName || '').toLowerCase();

    default:
      return '';
  }
};

// Sorts by the stack in order, each key carrying its own direction. An empty
// stack means "source order" and returns the input untouched.
//
// Keys are extracted once per row up front rather than inside the comparator:
// getComparableValue needs a mapped list item, and mapping is not cheap (it
// rebuilds a dozen fields). Comparator-time mapping cost ~2·n·log n mappings —
// at a few thousand rows that dominated the sort, and would have tripled again
// with three keys. Extracting first makes it n, so layering costs less than the
// old single-key sort did.
const sortTransactions = (transactions, sortKeys) => {
  if (!sortKeys?.length) return transactions;

  // mapTransferPartner=false: a paired transfer renders inside its anchor's row
  // and never sorts on its own, so the partner recursion is pure waste here.
  const decorated = transactions.map((transaction, index) => {
    const item = mapTransactionToListItem(transaction, false);
    return {
      transaction,
      index,
      keys: sortKeys.map((k) => getComparableValue(item, k.field)),
    };
  });

  decorated.sort((a, b) => {
    for (let depth = 0; depth < sortKeys.length; depth += 1) {
      const aValue = a.keys[depth];
      const bValue = b.keys[depth];

      // Blanks sort LAST in both directions, the way Excel does. That is why
      // the result is returned WITHOUT the direction flip below: negating it
      // for a descending sort would pull the blanks to the top, which is the
      // bug the old 'zzz' flag sentinel had.
      const aBlank = isBlankSortKey(aValue);
      const bBlank = isBlankSortKey(bValue);
      if (aBlank !== bBlank) return aBlank ? 1 : -1;
      // Both blank on this key — it can't order them, so fall to the next.
      if (aBlank) continue;

      let comparison = 0;

      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      if (comparison !== 0) {
        return sortKeys[depth].direction === 'desc' ? -comparison : comparison;
      }
    }

    // Fully tied on every key — fall back to source order so the sort is stable
    // regardless of engine behavior.
    return a.index - b.index;
  });

  return decorated.map((d) => d.transaction);
};

export { sortTransactions };

const MERCHANT_FIELD_TO_PAYLOAD_KEY = {
  merchantName: 'merchant_name',
  merchantWebsite: 'merchant_website',
  merchantPhone: 'merchant_phone',
  merchantStreetAddress: 'merchant_street_address',
  merchantCity: 'merchant_city',
  merchantState: 'merchant_state',
  merchantZip: 'merchant_zip'
};

// Build the PATCH /transactions/merchant payload, including only the fields the
// edit dialog actually sent. An omitted key means "leave the stored value
// alone": the :Merchant node is shared by every transaction pointing at it, so
// writing an untouched blank would wipe that value case-wide.
// A removal sends merchant_name: '' and nothing else. The server reads the
// empty name as "unlink" and only DELETEs the :MERCHANT / :MERCHANT_LOCATION
// relationships; the shared :Merchant node keeps every property it had, which
// is why no other key may ride along on a removal payload.
export const buildMerchantPayload = (merchant) => {
  if (merchant?.isRemoval) {
    return { merchant_name: '' };
  }

  return Object.entries(MERCHANT_FIELD_TO_PAYLOAD_KEY).reduce((payload, [field, key]) => {
    if (merchant?.[field] !== undefined) {
      payload[key] = merchant[field];
    }
    return payload;
  }, {});
};

// Bulk Edit Confirmation Dialog Component
const BulkEditConfirmDialog = ({ open, onClose, onConfirm, editType, editData, selectedTransactions }) => {
  const theme = useTheme();

  const getChangeDescription = () => {
    switch (editType) {
      case 'category':
        return `Change category to: ${editData.type} > ${editData.category} > ${editData.subcategory}`;
      case 'merchant':
        return editData.isRemoval
          ? `Remove the merchant assignment`
          : `Update merchant information`;
      case 'flag':
        // Check multiple possible property names for the flag
        const flagName = editData.flagName || editData.flag || editData.name;
        return flagName
          ? `Set flag to: ${flagName}`
          : 'Update flag';
      default:
        return 'Update transactions';
    }
  };

  const getMerchantChanges = () => {
    if (editData.isRemoval) {
      return ['Unlinks the merchant and its location from each selected transaction. The merchant itself is not deleted and stays available for other transactions.'];
    }

    const changes = [];
    if (editData.merchantName) changes.push(`Name: ${editData.merchantName}`);
    if (editData.merchantWebsite) changes.push(`Website: ${editData.merchantWebsite}`);
    if (editData.merchantPhone) changes.push(`Phone: ${editData.merchantPhone}`);
    if (editData.merchantStreetAddress) changes.push(`Address: ${editData.merchantStreetAddress}`);
    if (editData.merchantCity) changes.push(`City: ${editData.merchantCity}`);
    if (editData.merchantState) changes.push(`State: ${editData.merchantState}`);
    if (editData.merchantZip) changes.push(`ZIP: ${editData.merchantZip}`);
    return changes;
  };

  const getFlagChanges = () => {
    const changes = [];
    const flagName = editData.flagName || editData.flag || editData.name;
    if (flagName) changes.push(`Flag: ${flagName}`);
    if (editData.rationale) changes.push(`Rationale: ${editData.rationale}`);
    return changes;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm Bulk Edit</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Change Summary
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main' }}>
            <Typography variant="body1" fontWeight="bold">
              {getChangeDescription()}
            </Typography>
            {editType === 'category' && editData.rationale && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Rationale: {editData.rationale}
              </Typography>
            )}
            {editType === 'merchant' && (
              <Box sx={{ mt: 1 }}>
                {getMerchantChanges().map((change, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary">
                    • {change}
                  </Typography>
                ))}
              </Box>
            )}
            {editType === 'flag' && (
              <Box sx={{ mt: 1 }}>
                {getFlagChanges().map((change, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary">
                    • {change}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>
            Affected Transactions ({selectedTransactions.length})
          </Typography>
          <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
            {selectedTransactions.map((transaction, idx) => {
              const item = mapTransactionToListItem(transaction);
              return (
                <Box
                  key={transaction.transaction.id}
                  sx={{
                    mb: 1,
                    pb: 1,
                    borderBottom: idx < selectedTransactions.length - 1 ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2">
                    <strong>{formatDate(item.transactionDate)}</strong> - {item.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.bankAccountName} • {formatCurrency(item.amount)}
                  </Typography>
                </Box>
              );
            })}
          </Paper>
        </Box>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark">
            ⚠️ This action will update {selectedTransactions.length} transaction{selectedTransactions.length !== 1 ? 's' : ''}.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Confirm Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TransactionViewerByID = ({ transactionIDs, displayOptions }) => {
  // Fetch enriched transactions based on provided IDs
  const { data: transactions = [], isLoading } = useGetEnrichedTransactionsByIDList(transactionIDs, transactionIDs && transactionIDs.length > 0);
  //const filterableTransactions = transactions?.map(mapTransactionToFilterableTransaction) || [];
  const [filteredTransactions, setFilteredTransactions] = useState(transactions);
  const handleFilteredDataChange = (newFilteredData) => {
    console.log("New filtered data received:", newFilteredData);
    setFilteredTransactions(newFilteredData);
  };
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%', // Fill parent container
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden' // Prevent outer scrolling
      }}
    >
      {/* Filter Section - Compact and Fixed */}
      <Box sx={{
        flexShrink: 0,
        overflow: 'visible',
        zIndex: 1
      }}>
        <DataFilter
          data={transactions}
          onFilteredDataChange={handleFilteredDataChange}
          title="Filter Transactions"
          defaultCollapsed={true}
        >
          <TextSearchFilter
            fields={['description']}
            label="Description"
          />

          <DateRangeFilter
            field="transactionDate"
            label="Transaction Date"
          />

          <NumericRangeFilter
            field="amount"
            label="Amount"
            unit="$"
          />
        </DataFilter>
      </Box>
      <TransactionViewerDisplay
        transactions={filteredTransactions}
        isLoading={isLoading}
        displayOptions={displayOptions}
      />
    </Box>

  );
};

export const TransactionViewer = ({ transactions, isLoading, displayOptions, mutateTransactions, onSortedTransactionsChange, onSortDescriptionChange }) => {

  // Fetch all enriched transactions
  //const { data: transactions = [], isLoading } = useGetEnrichedTransactions();
  // const filterableTransactions = transactions?.map(mapTransactionToFilterableTransaction) || [];
  //const [filteredTransactions, setFilteredTransactions] = useState(transactions);

  // const handleFilteredDataChange = (newFilteredData) => {
  //   console.log("New filtered data received:", newFilteredData);
  //   setFilteredTransactions(newFilteredData);
  // };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%', // Fill parent container
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden' // Prevent outer scrolling
      }}
    >
      {/* Filter Section - Compact and Fixed
      <Box sx={{
        flexShrink: 0,
        overflow: 'visible',
        zIndex: 1
      }}>
        <DataFilter
          data={transactions}
          onFilteredDataChange={handleFilteredDataChange}
          title="Filter Transactions"
          defaultCollapsed={true}
        >
          <TextSearchFilter
            fields={['description']}
            label="Description"
          />

          <DateRangeFilter
            field="transactionDate"
            label="Transaction Date"
          />

          <NumericRangeFilter
            field="amount"
            label="Amount"
            unit="$"
          />
        </DataFilter>
      </Box> */}
      <TransactionViewerDisplay
        transactions={transactions}
        isLoading={isLoading}
        displayOptions={displayOptions}
        mutateTransactions={mutateTransactions}
        onSortedTransactionsChange={onSortedTransactionsChange}
        onSortDescriptionChange={onSortDescriptionChange}
      />
    </Box>
  );
};




// Memoized individual row component.
// Inline source-of-funds tags ([:OWNED_BY] entities, from the enriched query)
// plus the edit affordance — the TransactionViewer "Source" column. A 100% tag
// shows just the entity name; a partial tag appends the percent. Phase 2 fast-follow.
const SourceCellContent = ({ source, canEdit, onEdit }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
    {(source || []).map((s) => {
      // On-screen working view — opt into the entity's abbreviation, with the
      // full legal name on hover so the short form is never lossy.
      const name = formatEntityName(s, { abbreviate: true });
      const fullName = formatEntityName(s);
      const partial = s.pct != null && s.pct < 0.9999;
      return (
        <Chip
          key={s.entity_id}
          size="small"
          variant="outlined"
          title={fullName !== name ? fullName : undefined}
          label={partial ? `${name} ${formatPercentage(s.pct)}` : name}
        />
      );
    })}
    {canEdit && (
      <IconButton
        size="small"
        aria-label="Tag source of funds"
        onClick={onEdit}
        sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
      >
        <SourceIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

// Free-form user note on a transaction. Shows the note text (if any) with an
// edit affordance; when empty, contributors still see the pencil to add one.
const NotesCellContent = ({ notes, canEdit, onEdit }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
    {notes ? (
      <Typography
        variant="body2"
        title={notes}
        sx={{
          whiteSpace: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {notes}
      </Typography>
    ) : (
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {canEdit ? 'Add note' : ''}
      </Typography>
    )}
    {canEdit && (
      <IconButton
        size="small"
        aria-label={notes ? 'Edit note' : 'Add note'}
        onClick={onEdit}
        sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

// --- Contextual flag assessment (§4.8): flag rows explain themselves ----------
// A kept or withheld AI flag carries the assessment behind its edge: the
// §4.4 reason enum, a 1-2 sentence rationale, and the case-background line
// ids it cites (the "A3"/"C1" keys of the case briefing — rule 6: every
// decision cites what it rests on). Human verdicts (CONFIRMED) and pre-stage
// data carry NO assessment: those rows render exactly as they did before this
// feature — absence must never look broken.
//
// There is no citation-link component in this frontend yet (the briefing has
// no viewer; this is the first consumer of its line ids), so a cite renders as
// the design-system Badge the transfer verdict above already uses for compact
// state labels, with the line id explained on hover.
const FLAG_ASSESSMENT_REASON_LABELS = {
  ORDINARY_FOR_ACCOUNT: 'routine for this account',
  EXPLAINED_BY_RECORD: 'explained by the case records',
  EXPLAINED_BY_PROFESSIONAL: 'explained by a professional on the case',
  NOT_EXPLAINED: 'not explained by the case background',
  CONTRADICTED: 'contradicted by the case file',
};

const FlagAssessmentLine = ({
  flagStatus, assessment, canRestore, onRestore, rowDate, rowMerchant,
  // t_4553bc0c: true while ANY flag restore/undo mutation is in flight — the
  // table-scoped single-shot guard. Both Restore entry points (the verdict
  // line and the Read-more panel) render from ONE restoreButton, so one
  // prop covers both copies.
  actionInert,
}) => {
  // The Read-more panel anchors on its own trigger, so open/closed state lives
  // with the cell, not the row.
  const [detailsAnchor, setDetailsAnchor] = useState(null);
  // t_eb5adf39 item 2: the panel surface is named by its first line (the row
  // identity). useId mints one id per cell instance — this component mounts
  // once per row, so a static id would collide across rows.
  const headingId = useId();
  if (!assessment) return null;
  const withheld = flagStatus === 'SUPPRESSED';
  const because = assessment.rationale || FLAG_ASSESSMENT_REASON_LABELS[assessment.reason] || '';
  // A default-keep (lane 5) carries no reason and no cites by design (rule 1 —
  // there is nothing to say); show nothing rather than an empty sentence.
  if (!withheld && !because) return null;
  const cites = Array.isArray(assessment.cites) ? assessment.cites : [];
  const fullReason = because || 'by the case flag assessment';
  // A panel is worth opening only when it can say more than the clamped line.
  const hasDetails = Boolean(assessment.rationale) || cites.length > 0;
  // The row identity, built once (t_eb5adf39 item 2): the Restore aria-label,
  // the panel's first line and the surface's accessible name all read from
  // this same value — what a screen reader hears and what the panel shows
  // cannot drift apart.
  const rowLabel = [formatDate(rowDate), rowMerchant].filter(Boolean).join(' ');
  // §4.6 reviewer restore: one click, no dialog — the flag becomes a human
  // verdict, final (the SUPPRESSED audit edge and the assessment stay on the
  // trail; this row stops showing the suppressed one). One handler, two entry
  // points: pinned to the verdict line AND at the foot of the panel.
  const restoreButton = withheld && canRestore ? (
    <Button
      size="small"
      variant="text"
      aria-label={`Restore flag for ${rowLabel}`}
      onClick={onRestore}
      disabled={Boolean(actionInert)}
      sx={flagActionSx({ minWidth: 0, px: 0.75, py: 0, fontSize: 'var(--text-xs)', textTransform: 'none' }, actionInert)}
    >
      Restore
    </Button>
  ) : null;
  return (
    <>
    <Box
      sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, maxWidth: 260 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Line 1: verdict left, action right. Rationales run 2-4 sentences, so
          the action never sits below the text it belongs to. */}
      <Box
        data-flag-line="verdict"
        sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <Typography
          sx={{
            fontSize: 'var(--text-xs)',
            lineHeight: 'var(--leading-snug)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--text-primary)',
          }}
        >
          {withheld ? 'Withheld' : 'Kept'}
        </Typography>
        {restoreButton}
      </Box>
      {/* Line 2: the reason, clamped to two lines; the whole text and the cites
          it rests on go to the panel (rule 6: every decision cites its basis). */}
      <Box data-flag-line="reason" sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography
          data-flag-rationale="clamped"
          sx={{
            fontSize: 'var(--text-xs)',
            lineHeight: 'var(--leading-snug)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {fullReason}
        </Typography>
        {hasDetails && (
          <Button
            size="small"
            variant="text"
            aria-haspopup="dialog"
            aria-expanded={Boolean(detailsAnchor)}
            onClick={(e) => setDetailsAnchor(e.currentTarget)}
            sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 'var(--text-xs)', textTransform: 'none' }}
          >
            Read more
          </Button>
        )}
      </Box>
    </Box>
    {/* t_eb5adf39 item 3 (structure): the panel is rendered OUTSIDE the cell's
        guarded Box because in production MUI Portals it to document.body — the
        cell's DOM guard has never covered a panel click there; the guard that
        protects the panel is the one on its own surface (it lands on the
        Modal root div, the Paper's DOM parent). As the cell's sibling, the
        panel's clicks are guarded by the panel exactly as the row's clicks
        are guarded by the cell — and the propagation test can pin it. */}
      {hasDetails && (
        <Popover
          data-flag-details="panel"
          role="dialog"
          aria-labelledby={rowLabel ? headingId : undefined}
          aria-label={rowLabel ? undefined : 'Flag assessment for this transaction'}
          open={Boolean(detailsAnchor)}
          anchorEl={detailsAnchor}
          onClose={() => setDetailsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // Rows are clickable: a click inside the panel is not a click on the
          // transaction.
          onClick={(e) => e.stopPropagation()}
          PaperProps={{
            sx: {
              p: 2,
              // Readable measure for the full rationale, on the 4px spacing
              // scale (90 / 130 steps = 360px / 520px) rather than a raw px.
              minWidth: 'calc(var(--space-1) * 90)',
              maxWidth: 'calc(var(--space-1) * 130)',
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* t_eb5adf39 item 2: the panel opens with the row it belongs to.
                A 332-char rationale about one transaction must never read as
                belonging to its neighbour (the trigger can be off-screen and
                MUI clamps the paper away from it); this line is also the
                surface's accessible name, answering the trigger's
                aria-haspopup="dialog". */}
            {rowLabel && (
              <Typography
                id={headingId}
                data-flag-details="heading"
                sx={{
                  fontSize: 'var(--text-sm)',
                  lineHeight: 'var(--leading-normal)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                {rowLabel}
              </Typography>
            )}
            <Typography sx={{ fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', whiteSpace: 'pre-wrap' }}>
              {fullReason}
            </Typography>
            {cites.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {cites.map((lineId) => (
                  <Tooltip
                    key={lineId}
                    title={`Case-background line ${lineId}, cited by this flag assessment`}
                  >
                    <Badge variant="info">{lineId}</Badge>
                  </Tooltip>
                ))}
              </Box>
            )}
            {restoreButton}
          </Box>
        </Popover>
      )}
    </>
  );
};

// t_4a1821f3 (Alex: "let's see how that looks") — the flag-restore Undo must
// read as a button at a glance, and the two placements (the post-restore
// confirmation's action, the restored row cell's line) are ONE action: they
// carry ONE shared treatment, defined here and spread at both call sites so
// they cannot drift apart. The white-filled outlined control read as a chip
// or a hole; this is the app's primary action surface — contained + theme
// primary (colors.navy[700] #334e68 fill, colors.white label = 8.64:1, WCAG
// AAA), the same surface every Save/Confirm in the app uses.
// t_4a7318f7 item 3 (review note on cd08e6a40): the theme carries the
// button, so only what the theme does NOT provide stays here. Checked
// against muiTheme.js: the 40px target and padding come from
// MuiButton.styleOverrides.root (:171-177, minHeight 40 + padding 8px 20px —
// the deleted sx px 2 undercut that padding), and text-transform none plus
// the 13px label come from typography.button (:118-123, which MUI's Button
// root spreads into its own styles — node_modules Button.js:69). The theme's
// label weight is medium; the operator accepted semibold, so that value, and
// only that value, is kept.
const FLAG_RESTORE_UNDO_PROPS = {
  variant: 'contained',
  color: 'primary',
  sx: { fontWeight: 'var(--font-semibold)' },
};

// t_4553bc0c fix 1 — the in-flight treatment for EVERY flag action control.
// The reported defect is not a double-fire on one control: the first click's
// optimistic update removes the row, the list shifts up, and the second click
// of a double-click lands on the NEXT row's control at the same screen point.
// A per-control guard therefore cannot hold — while any flag restore/undo
// mutation is in flight, the whole action lane must be inert. One merge
// point, so no control can forget the pair: `disabled` makes the control
// non-interactive, `cursor: default` + `pointer-events: none` make the lane
// read and hit-test as inert — the click falls through to the flag cell,
// whose own stopPropagation guard (t_423cdb24) swallows it rather than
// transferring it to a neighbour or opening the row. Non-inert returns the
// base object UNCHANGED: the shared Undo treatment is pinned by reference
// identity in the parity test, and this helper must not break that.
const FLAG_ACTION_INERT_SX = { cursor: 'default', pointerEvents: 'none' };
const flagActionSx = (baseSx, inert) => (inert ? { ...baseSx, ...FLAG_ACTION_INERT_SX } : baseSx);

// Undo of a reviewer restore (t_892abb39): a live human CONFIRMED verdict that
// still carries the stage's SUPPRESSED audit edge is a restored flag — the read
// says so with flag_restored (api/transactions.js deriveIsRestoredFlag; the
// pairing is computed from the graph, never from the row's own props). The cell
// offers the exact inverse of the §4.6 restore at the same place the restore
// sits: one click withdraws the CONFIRMED edge and the row falls back to
// withheld with its reason (the audit edge and its assessment never left). An
// ordinary human flag — a CONFIRMED edge with no audit trail — shows nothing
// here, exactly as rule 2 intends. There is deliberately no Read-more panel on
// this line: a human verdict carries no assessment (rule 2) so there are no
// cites to open, and the withheld reason the row used to show stands back up
// the moment the undo lands (the post-restore confirmation's Undo revalidates
// the withheld view into exactly that state).
const FlagRestoredLine = ({ canRestore, onUndo, rowDate, rowMerchant, actionInert }) => (
  <Box
    sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: 260 }}
    onClick={(e) => e.stopPropagation()}
  >
    <Box
      data-flag-line="verdict"
      data-flag-restored="line"
      sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
    >
      {/* t_4a7318f7 item 2 (operator vision pass on cd08e6a40): the filled
          Undo must not outrank the state it acts on. Measured first (see
          agent_code/scratch/flag_chip_contrast.py in the journal): this
          label already rendered gray-900 at 17.72:1 — the "low contrast"
          report was the vision model's impression, not the surface. What
          the line lacked was reading AS a status: it now carries the app's
          human-verdict status colour — the same success-700 label the
          "Confirmed" transfer Badge uses, the same green the restore toast
          announces this state in. 7.59:1 on white, 6.28:1 on the darkest
          row tint — AA everywhere. */}
      <Typography
        sx={{
          fontSize: 'var(--text-xs)',
          lineHeight: 'var(--leading-snug)',
          fontWeight: 'var(--font-semibold)',
          color: 'var(--color-success-700)',
        }}
      >
        Restored
      </Typography>
      {canRestore && (
        <Button
          {...FLAG_RESTORE_UNDO_PROPS}
          disabled={Boolean(actionInert)}
          sx={flagActionSx(FLAG_RESTORE_UNDO_PROPS.sx, actionInert)}
          aria-label={`Undo restore of flag for ${[formatDate(rowDate), rowMerchant].filter(Boolean).join(' ')}`}
          onClick={onUndo}
        >
          Undo
        </Button>
      )}
    </Box>
  </Box>
);

// t_eb5adf39 item 1 — the flag/action column is the table's LAST column,
// pinned to its right edge (Alex's option b): Restore, Read more and Undo
// must stay reachable at laptop width with the chat panel open and zero
// horizontal scrolling, at every scroll offset. The pin must exist at EVERY
// render site — header cell, virtualized main row, transfer-partner row,
// single-row branch — or the column drifts. Two structural requirements ride
// on it:
//   - Opaque base + per-state tint layers, so cells sliding under the pinned
//     column never show through. A tint is painted as a background-image over
//     the surface colour: the same alpha over the same surface composites to
//     exactly what the neighbouring cells render.
//   - border-collapse: separate on each row's table box: under the collapsing
//     model a cell's borders are painted on the table grid, not on the cell,
//     so a sticky-shifted cell would lose its row rule (the same reason MUI
//     stickyHeader switches a Table to 'separate').
// The header and the rows live in different scroll containers (the header is
// clipped by the Paper; rows scroll inside react-window's List), so on
// classic-scrollbar platforms the List's vertical scrollbar insets the body's
// right edge and the header must follow by the same amount. Promising a
// `scrollbar-gutter: stable` on the header box was tried first and failed in
// host Chrome (t_537cda9b): the property is emitted but reserves nothing on a
// content-height, never-overflowing box. The header wrapper is instead inset
// by the List's MEASURED scrollbar width — see the listRef block in
// TransactionViewerDisplay. Print and Excel build their own column lists and
// are unaffected.
const FLAG_PIN_POSITION = { position: 'sticky', right: 0, zIndex: 2 };
// t_2473e39d: ONE width source for the pinned flag column — the header cell
// and all three row shapes must render the same box or the pin drifts (this
// file's recurring failure mode; a source-level test pins the four sites).
// The value is open (widen vs wrap is the owner's call); changing it changes
// this line only. The full flag name is always available regardless of the
// cell's visible text: the DOM label carries it (ellipsis is CSS-only, so the
// accessible name is complete) and title puts it on hover.
const FLAG_COLUMN_WIDTH = '150px';
const flagTintLayer = (tint) => `linear-gradient(${tint}, ${tint})`;

// The flag pill's honesty rule (t_4a7318f7 item 1, extended to the detail
// dialog by t_1a266924) lives in ONE shared source — utils/flagWithheld.js,
// imported above — with the dialog chip and the export text resolving
// through it too, so the same flag can never tell a different story by
// surface. The rationale (muted-vs-red, the defensive !flag_restored half,
// the measured contrast, why partner rows are red) is there.

// react-window v2's `rowComponent` is a plain function component — it does
// NOT receive a React ref (verified against react-window v2.1's d.ts). The
// internal rowRef below is purely for our own ResizeObserver-based height
// measurement, attached directly to the outer <div>.
const VirtualTransactionRow = memo(({ index, style, data }) => {
  const rowRef = useRef(null);
  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext?.userProfile;

  const { onRowMeasured, measurementVersion } = data;

  // Add this to your VirtualTransactionRow component
const resizeObserverRef = useRef(null);

useLayoutEffect(() => {
  if (rowRef.current && onRowMeasured) {
    // Disconnect previous observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    // Create new ResizeObserver
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        onRowMeasured(index, height);
      }
    });

    // Observe the row
    resizeObserverRef.current.observe(rowRef.current);

    // Initial measurement using scrollHeight
    const initialHeight = rowRef.current.scrollHeight;
    onRowMeasured(index, initialHeight);
  }

  // Cleanup
  return () => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }
  };
}, [index, onRowMeasured, measurementVersion]);

  const {
    sortedTransactions,
    theme,
    displayOptions,
    handleClickOpen,
    handleEditSubcategory,
    handleEditMerchant,
    handleEditFlag,
    handleEditSource,
    handleEditNotes,
    handleEditDescription,
    handleDeleteFlagClicked,
    handleRestoreFlag,
    handleUndoRestoreFlag,
    canRestoreFlags,
    flagActionPending,
    handleReviewMatches,
    mapTransactionToListItem,
    formatDate,
    formatCurrency,
    renderSubcategoryCell,
    selectedTransactionIDs,
    handleToggleSelection
  } = data;

  const transaction = sortedTransactions[index];
  const item = mapTransactionToListItem(transaction);
  const isSelected = selectedTransactionIDs.has(transaction.transaction.id);

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    handleToggleSelection(transaction.transaction.id);
  };

  // Handle transfer partner rendering
  if (item.transferPartner) {
    const partnerSelected = transaction.transfer_partner
      ? selectedTransactionIDs.has(transaction.transfer_partner.transaction.id)
      : false;

    return (
      <div style={style} ref={rowRef}>
        {/* The MUI TableRow renders an HTML <tr>, which must live inside a
            <table><tbody> for valid DOM nesting. react-window absolutely
            positions our outer <div> (so we can't make it the table itself);
            the inner table fills the div via width:100% and the existing
            sx:{display:'table',tableLayout:'fixed'} on each TableRow keeps
            column alignment with the header table above the virtualized list. */}
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
            // Hovering EITHER leg lights up the whole pair. The pairing is the
            // point of this row group, so the hover affordance has to follow
            // the pair rather than the individual row — highlighting one leg
            // alone re-introduces the "which rows belong together?" doubt this
            // group styling exists to remove. A selected leg keeps its deeper
            // value so selection stays legible while the pair is lit.
            '&:hover [data-transfer-leg]': {
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
            '&:hover [data-transfer-leg][data-leg-selected="true"]': {
              backgroundColor: alpha(theme.palette.primary.main, 0.24),
            },
            // The pinned flag cell sits above the cells sliding under it, so
            // it restates the pair's tint states itself: the same alpha over
            // the surface colour composites to what the row renders.
            '&:hover [data-flag-pinned]': {
              backgroundImage: flagTintLayer(alpha(theme.palette.primary.main, 0.16)),
            },
            '&:hover [data-leg-selected="true"] [data-flag-pinned]': {
              backgroundImage: flagTintLayer(alpha(theme.palette.primary.main, 0.24)),
            },
          }}
        >
          <tbody>
        {/* Main transaction row (the out-leg).

            Both legs share one accent bar + tint and the block is closed by a
            bottom rule under the partner row, so the pair reads as a single
            enclosed group. Previously the strongest edges were a 2px rule
            BETWEEN the legs and a left bar on the partner only — an open
            bracket that made the partner look grouped with the NEXT
            transaction instead of with its own counterpart. */}
        <TableRow
          onClick={() => handleClickOpen(transaction)}
          data-transfer-leg
          data-leg-selected={isSelected ? 'true' : 'false'}
          sx={{
            cursor: 'pointer',
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'separate', // pinned flag cell keeps its own borders (see FLAG_PIN_POSITION)
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            backgroundColor: isSelected
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <TableCell padding="checkbox" sx={{ width: '48px' }}>
            <Checkbox
              checked={isSelected}
              onChange={handleCheckboxClick}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
          <TableCell sx={{ width: '100px' }}>
            {formatDate(item.transactionDate)}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <TransferOutIcon
                sx={{ fontSize: 14, color: theme.palette.primary.main, mr: 0.5 }}
                aria-hidden="true"
              />
              <Typography variant="caption" color="primary">
                Internal Transfer Out
              </Typography>
            </Box>
          </TableCell>
          <TableCell sx={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.bankAccountName}</TableCell>
          {displayOptions?.showCardholder && (
            <TableCell sx={{ width: '150px' }}>
              <CardholderCell isCard={item.isCardRow} name={item.cardholderName} ending={item.cardEnding} />
            </TableCell>
          )}
          <TableCell sx={{ width: '300px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DescriptionCell
                  item={item}
                  canEdit={userProfile?.isCaseContributor()}
                  onEdit={(e) => handleEditDescription(e, transaction)} />
              </Box>
              {/* Transfer verdict: disposition badge + confirm/reject, kept together
                  in the roomy description cell so nothing overlaps the date column. */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Badge variant={item.transferManual ? 'accent' : item.transferDisposition === 'CONFIRMED' ? 'success' : 'warning'}>
                  {item.transferManual ? 'Manual' : item.transferDisposition === 'CONFIRMED' ? 'Confirmed' : 'Proposed'}
                </Badge>
                {/* Confirm / reject happen inside the review dialog — you should
                    see the full candidate set before deciding. */}
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ReviewMatchIcon fontSize="small" />}
                  onClick={(e) => handleReviewMatches(e, transaction)}
                  sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 'var(--text-xs)', textTransform: 'none' }}
                >
                  Review
                </Button>
              </Box>
            </Box>
          </TableCell>
          <TableCell sx={{
            width: '100px',
            color: item.amount < 0 ? theme.palette.error.main : theme.palette.success.main,
            fontWeight: 'bold'
          }}>
            {formatCurrency(item.amount)}
          </TableCell>
          {displayOptions?.showMerchant && (
            <TableCell sx={{ width: '200px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.merchantName && (
                  <>
                    {item.merchantName}
                  </>
                )}
                {userProfile?.isCaseContributor() &&
                <IconButton
                  size="small"
                  onClick={(e) => handleEditMerchant(e, transaction)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                }

              </Box>
            </TableCell>
          )}
          {renderSubcategoryCell(item, transaction)}
          {displayOptions?.showSource && (
            <TableCell sx={{ width: '120px' }}>
              <SourceCellContent
                source={transaction.source}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditSource(e, transaction)} />
            </TableCell>
          )}
          {displayOptions?.showNotes && (
            <TableCell sx={{ width: '200px' }}>
              <NotesCellContent
                notes={transaction.transaction?.notes}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditNotes(e, transaction)} />
            </TableCell>
          )}
          {displayOptions?.showFlag && (
            <TableCell
              data-flag-pinned
              sx={{
                width: FLAG_COLUMN_WIDTH,
                ...FLAG_PIN_POSITION,
                backgroundColor: 'var(--surface-primary)',
                backgroundImage: flagTintLayer(alpha(theme.palette.primary.main, isSelected ? 0.12 : 0.04)),
              }}
            >
              {item.flagName && (
                <Chip
                  icon={<WarningIcon />}
                  label={item.flagName}
                  title={item.flagName}
                  color={flagChipColor(transaction)}
                  size="small"
                />
              )}
              {userProfile?.isCaseContributor() &&
              <IconButton
                size="small"
                onClick={(e) => handleEditFlag(e, transaction)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              }
              {userProfile?.isCaseContributor() && item.flagName && (
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteFlagClicked(e, transaction)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {/* §4.8: the flag's own assessment explains it (kept rows carry
                  one; a withheld row also gets the one-click §4.6 restore). */}
              <FlagAssessmentLine
                flagStatus={transaction.flag_status}
                assessment={transaction.flag_assessment}
                canRestore={canRestoreFlags && Boolean(userProfile?.isCaseContributor())}
                onRestore={(e) => handleRestoreFlag(e, transaction)}
                rowDate={item.transactionDate}
                rowMerchant={item.merchantName}
                actionInert={flagActionPending}
              />
              {/* t_892abb39: a restored flag (live verdict + surviving audit
                  trail) gets its one-click inverse here; an ordinary human
                  flag shows nothing extra. Same gating shape as the sibling
                  line: the state reads to every role, the action is a
                  contributor's. */}
              {deriveIsRestoredFlag(transaction) && (
                <FlagRestoredLine
                  canRestore={canRestoreFlags && Boolean(userProfile?.isCaseContributor())}
                  onUndo={(e) => handleUndoRestoreFlag(e, transaction)}
                  rowDate={item.transactionDate}
                  rowMerchant={item.merchantName}
                  actionInert={flagActionPending}
                />
              )}

            </TableCell>
          )}
        </TableRow>

        {/* Transfer partner row */}
        <TableRow
          onClick={() => handleClickOpen(transaction)}
          data-transfer-leg
          data-leg-selected={partnerSelected ? 'true' : 'false'}
          sx={{
            cursor: 'pointer',
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'separate', // pinned flag cell keeps its own borders (see FLAG_PIN_POSITION)
            backgroundColor: partnerSelected
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.primary.main, 0.04),
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            // Closes the pair. The tint and bar stop here, so the next
            // transaction below is plainly outside the group.
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          <TableCell padding="checkbox" sx={{ width: '48px' }}>
            <Checkbox
              checked={partnerSelected}
              onChange={(e) => {
                e.stopPropagation();
                if (transaction.transfer_partner) {
                  handleToggleSelection(transaction.transfer_partner.transaction.id);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
          <TableCell sx={{ width: '100px' }}>
            <Box sx={{ display: 'flex', alignItems: 'stretch', pl: 1 }}>
              {/* Dashed rule running the height of the row: the in-leg is an
                  indented sub-row of the out-leg above it, not a peer of the
                  transaction below. */}
              <Box sx={{
                borderLeft: `1px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
                mr: 1
              }} />
              <Box>
                {formatDate(item.transferPartner.transactionDate)}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TransferInIcon
                    sx={{ fontSize: 14, color: theme.palette.primary.main, mr: 0.5 }}
                    aria-hidden="true"
                  />
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                    Internal Transfer In
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TableCell>
          <TableCell sx={{
            width: '124px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic',
            color: 'text.secondary'
          }}>
            {item.transferPartner.bankAccountName}
          </TableCell>
          {displayOptions?.showCardholder && (
            <TableCell sx={{ width: '150px' }}>
              <CardholderCell
                isCard={item.transferPartner.isCardRow}
                name={item.transferPartner.cardholderName}
                ending={item.transferPartner.cardEnding}
              />
            </TableCell>
          )}
          <TableCell sx={{
            width: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontStyle: 'italic',
            color: 'text.secondary'
          }}>
            <DescriptionCell
              item={item.transferPartner}
              canEdit={userProfile?.isCaseContributor()}
              onEdit={(e) => handleEditDescription(e, transaction.transfer_partner)} />
          </TableCell>
          <TableCell sx={{
            width: '100px',
            color: item.transferPartner.amount < 0 ? theme.palette.error.main : theme.palette.success.main,
            fontWeight: 'bold'
          }}>
            {formatCurrency(item.transferPartner.amount)}
          </TableCell>
          {displayOptions?.showMerchant && (
            <TableCell sx={{ width: '200px', fontStyle: 'italic', color: 'text.secondary' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.transferPartner.merchantName && (
                  <>
                    {item.transferPartner.merchantName}
                  </>
                )}
                {userProfile?.isCaseContributor() &&
                <IconButton
                  size="small"
                  onClick={(e) => handleEditMerchant(e, transaction, true)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
  }
              </Box>
            </TableCell>
          )}
          {renderSubcategoryCell(item.transferPartner, transaction, true)}
          {displayOptions?.showSource && (
            <TableCell sx={{ width: '120px' }}>
              <SourceCellContent
                source={transaction.transfer_partner?.source}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditSource(e, transaction.transfer_partner)} />
            </TableCell>
          )}
          {displayOptions?.showNotes && (
            <TableCell sx={{ width: '200px' }}>
              <NotesCellContent
                notes={transaction.transfer_partner?.transaction?.notes}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditNotes(e, transaction.transfer_partner)} />
            </TableCell>
          )}
          {displayOptions?.showFlag && (
            <TableCell
              data-flag-pinned
              sx={{
                width: FLAG_COLUMN_WIDTH,
                ...FLAG_PIN_POSITION,
                backgroundColor: 'var(--surface-primary)',
                backgroundImage: flagTintLayer(alpha(theme.palette.primary.main, partnerSelected ? 0.12 : 0.04)),
              }}
            >
              {item.transferPartner.flagName && (
                <Chip
                  icon={<WarningIcon />}
                  label={item.transferPartner.flagName}
                                    title={item.transferPartner.flagName}
color={flagChipColor(transaction.transfer_partner)}
                  size="small"
                  sx={{ opacity: 0.8 }}
                />
              )}
              {userProfile?.isCaseContributor() &&
              <IconButton
                size="small"
                onClick={(e) => handleEditFlag(e, transaction, true)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
  }
              {userProfile?.isCaseContributor() && item.transferPartner.flagName && (
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteFlagClicked(e, transaction, true)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </TableCell>
          )}
        </TableRow>
          </tbody>
        </Box>
      </div>
    );
  }

  // Single transaction row — same table/tbody wrap as the transferPartner
  // branch above, see comment there for why.
  return (
    <div style={style} ref={rowRef}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <tbody>
      <TableRow
        onClick={() => handleClickOpen(transaction)}
        hover
        sx={{
          cursor: 'pointer',
          display: 'table',
          width: '100%',
          tableLayout: 'fixed',
          borderCollapse: 'separate', // pinned flag cell keeps its own borders (see FLAG_PIN_POSITION)
          '&:hover': { backgroundColor: theme.palette.action.hover },
          '&:hover [data-flag-pinned]': {
            backgroundImage: flagTintLayer(theme.palette.action.hover),
          },
          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'inherit',
        }}
      >
        <TableCell padding="checkbox" sx={{ width: '48px' }}>
          <Checkbox
            checked={isSelected}
            onChange={handleCheckboxClick}
            onClick={(e) => e.stopPropagation()}
          />
        </TableCell>
        <TableCell sx={{ width: '100px' }}>
          {formatDate(item.transactionDate)}
        </TableCell>
        <TableCell sx={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.bankAccountName}</TableCell>
        {displayOptions?.showCardholder && (
          <TableCell sx={{ width: '150px' }}>
            <CardholderCell isCard={item.isCardRow} name={item.cardholderName} ending={item.cardEnding} />
          </TableCell>
        )}
        <TableCell sx={{ width: '300px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DescriptionCell
                item={item}
                canEdit={userProfile?.isCaseContributor()}
                onEdit={(e) => handleEditDescription(e, transaction)} />
            </Box>
            {/* An unpaired transfer-categorized txn still gets a Review entry
                point — to match it, or restore a pair the reviewer unmatched. */}
            {item.isTransferCategorized && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <Badge variant="warning">Unmatched</Badge>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ReviewMatchIcon fontSize="small" />}
                  onClick={(e) => handleReviewMatches(e, transaction)}
                  sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 'var(--text-xs)', textTransform: 'none' }}
                >
                  Review
                </Button>
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{
          width: '100px',
          color: item.amount < 0 ? theme.palette.error.main : theme.palette.success.main,
          fontWeight: 'bold'
        }}>
          {formatCurrency(item.amount)}
        </TableCell>
        {displayOptions?.showMerchant && (
          <TableCell sx={{ width: '200px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {item.merchantName && (
                <>
                  {item.merchantName}
                </>
              )}
              {userProfile?.isCaseContributor() &&
              <IconButton
                size="small"
                onClick={(e) => handleEditMerchant(e, transaction)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
}
            </Box>
          </TableCell>
        )}
        {renderSubcategoryCell(item, transaction)}
        {displayOptions?.showSource && (
          <TableCell sx={{ width: '120px' }}>
            <SourceCellContent
              source={transaction.source}
              canEdit={userProfile?.isCaseContributor()}
              onEdit={(e) => handleEditSource(e, transaction)} />
          </TableCell>
        )}
        {displayOptions?.showNotes && (
          <TableCell sx={{ width: '200px' }}>
            <NotesCellContent
              notes={transaction.transaction?.notes}
              canEdit={userProfile?.isCaseContributor()}
              onEdit={(e) => handleEditNotes(e, transaction)} />
          </TableCell>
        )}
        {displayOptions?.showFlag && (
          <TableCell
            data-flag-pinned
            sx={{
              width: FLAG_COLUMN_WIDTH,
              ...FLAG_PIN_POSITION,
              backgroundColor: 'var(--surface-primary)',
              ...(isSelected ? { backgroundImage: flagTintLayer(alpha(theme.palette.primary.main, 0.08)) } : null),
            }}
          >
            {item.flagName && (
              <Chip
                icon={<WarningIcon />}
                label={item.flagName}
                title={item.flagName}
                color={flagChipColor(transaction)}
                size="small"
              />
            )}
            {userProfile?.isCaseContributor() &&
            <IconButton
              size="small"
              onClick={(e) => handleEditFlag(e, transaction)}
              sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
}
            {userProfile?.isCaseContributor() && item.flagName && (
              <IconButton
                size="small"
                onClick={(e) => handleDeleteFlagClicked(e, transaction)}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            {/* §4.8: the flag's own assessment explains it (kept rows carry
                one; a withheld row also gets the one-click §4.6 restore). */}
            <FlagAssessmentLine
              flagStatus={transaction.flag_status}
              assessment={transaction.flag_assessment}
              canRestore={canRestoreFlags && Boolean(userProfile?.isCaseContributor())}
              onRestore={(e) => handleRestoreFlag(e, transaction)}
              rowDate={item.transactionDate}
              rowMerchant={item.merchantName}
              actionInert={flagActionPending}
            />
            {/* t_892abb39: a restored flag (live verdict + surviving audit
                trail) gets its one-click inverse here; an ordinary human
                flag shows nothing extra. Same gating shape as the sibling
                line: the state reads to every role, the action is a
                contributor's. */}
            {deriveIsRestoredFlag(transaction) && (
              <FlagRestoredLine
                canRestore={canRestoreFlags && Boolean(userProfile?.isCaseContributor())}
                onUndo={(e) => handleUndoRestoreFlag(e, transaction)}
                rowDate={item.transactionDate}
                rowMerchant={item.merchantName}
                actionInert={flagActionPending}
              />
            )}
          </TableCell>
        )}
      </TableRow>
        </tbody>
      </table>
    </div>
  );
});

const TransactionViewerDisplay = ({ transactions, isLoading, displayOptions, mutateTransactions, onSortedTransactionsChange, onSortDescriptionChange }) => {
  // NOTE: do NOT console.log the full `transactions` array here. It runs on
  // every render and Chromium retains the logged reference indefinitely (never
  // GC'd), so on a large case the tab leaks memory monotonically until it OOMs.
  // It also dumped transaction PII (names, descriptions, amounts) to the console.
  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext?.userProfile;
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  // Layered sort stack, index 0 = primary key. See nextSortKeys for the rules.
  const [sortKeys, setSortKeys] = useState(DEFAULT_SORT_KEYS);

  // Selection state
  const [selectedTransactionIDs, setSelectedTransactionIDs] = useState(new Set());

  // for dynamic row height measurement for the react-window list
  const [measuredHeights, setMeasuredHeights] = useState(new Map());
  const [measurementVersion, setMeasurementVersion] = useState(0);

  const prevTransactionsRef = useRef([]);
  const prevSortSignatureRef = useRef(sortKeysSignature(DEFAULT_SORT_KEYS));
  const prevPairingSignatureRef = useRef('');

  // Pinned Flag column: keep the header cell above its body cells
  // (t_537cda9b). The header and the rows are separate scroll containers, so
  // on classic-scrollbar platforms the List's vertical scrollbar insets the
  // body's right edge while the header has nothing to inset it by — the
  // shipped `scrollbar-gutter: stable` promise reserves nothing on the
  // never-overflowing header box (measured in host Chrome at 7d565e16a: the
  // header cell sat exactly one 15px scrollbar right of its cells; the
  // property IS emitted — running the installed emotion serializer on the sx
  // object prints scrollbar-gutter:stable — the platform just does not
  // honour it here). Rather than promise a gutter, mirror the List's ACTUAL
  // right inset: offsetWidth - clientWidth on its outer element, reached
  // through react-window 2.1's listRef imperative API (`api.element` is the
  // scroller node itself — verified in the installed dist and pinned by a
  // test). The measured value is 0 on overlay-scrollbar platforms and
  // whenever the rows don't overflow vertically, so the header is never
  // inset when the body isn't either. That makes the formula correct only
  // at the moment it runs — keeping the header current needs a re-measure
  // whenever the feed's OVERFLOW STATE flips, not just when the scroller's
  // box changes; see the sizer observation in syncHeaderInset (t_2bb1b175).
  //
  // The value NEVER goes through React state (t_c6346a75): state → layout
  // (the inset drives the wrapper's margin) → observation (this effect and
  // the ResizeObserver re-measure the layout they caused) → state is a
  // cycle, and in a real browser the measurement is not invariant across
  // re-renders — the List's vertical-scrollbar state couples with the
  // per-row ResizeObserver's dynamic row heights — so the loop never
  // settles and React aborts the page with "Maximum update depth
  // exceeded". The measurement instead writes the CSS custom property
  // `--flag-header-inset` straight onto the header wrapper node, which
  // reads it back as its own marginRight: nothing re-renders, no cycle by
  // construction. The host is a plain element with its own ref — the
  // design-system Box cannot carry one under the repo-wide MUI mock, and
  // the write wiring is pinned by a jsdom test (the row containers at
  // :1114/:1516 set the same precedent: refs live on plain elements).
  const listScrollerRef = useRef(null);
  const headerInsetHostRef = useRef(null);
  const insetObserverRef = useRef(null);
  const observedScrollerRef = useRef(null);
  const observedSizerRef = useRef(null);

  // offsetWidth/clientWidth are absent in jsdom (no layout engine) — coerce
  // so the written value is always a real px number; the test pins the
  // write, not the pixel.
  const applyHeaderInset = useCallback(() => {
    const host = headerInsetHostRef.current;
    if (!host) return;
    const scroller = listScrollerRef.current;
    const inset = scroller
      ? Math.max(0, (scroller.offsetWidth || 0) - (scroller.clientWidth || 0))
      : 0; // List gone (loading skeleton, unmount): explicit reset.
    host.style.setProperty('--flag-header-inset', `${inset}px`);
  }, []);

  // Measure + (re)attach the observers by element identity. Idempotent, so
  // every trigger below can call it without churn.
  const syncHeaderInset = useCallback(() => {
    applyHeaderInset();
    const scroller = listScrollerRef.current;
    if (scroller !== observedScrollerRef.current) {
      observedScrollerRef.current = scroller;
      observedSizerRef.current = null;
      if (insetObserverRef.current) {
        insetObserverRef.current.disconnect();
        insetObserverRef.current = null;
      }
      // Container resizes (the chat panel opening, window resize, page
      // layout) change the scroller's box without a React render — the
      // observer catches those and writes through the same DOM-only path.
      if (scroller) {
        const observer = new ResizeObserver(applyHeaderInset);
        observer.observe(scroller);
        insetObserverRef.current = observer;
      }
    }
    if (!scroller || !insetObserverRef.current) return;
    // The box observation above can never fire when a feed merely SHRINKS
    // below the viewport: the scroller's box is unchanged, only its content
    // height is — the vertical scrollbar disappears, the body drops its
    // inset, and a header still holding the stale value sits one gutter
    // width LEFT of its cells (measured in a one-row feed at f0f599e8f:
    // dx=-15, t_2bb1b175). react-window keeps the total content height in
    // the scroller's LAST child — an aria-hidden sizer div with an inline
    // height (contract pinned against the installed dist by a test) — so
    // observing THAT element fires exactly on content-height changes, from
    // any cause (row count, row re-measurement, a pairing flip). A
    // ResizeObserver callback runs after the frame's layout, when the read
    // reflects the settled scrollbar state, so the header follows the
    // body's overflow state instead of lagging it. The explicit
    // border-box: the sizer has no padding, no border, no scrollbar — the
    // observed box is its content height unambiguously on every engine.
    // Re-attaching on a sizer identity change is also self-correcting:
    // observing an element delivers its current size, so a remounted sizer
    // re-measures the overflow state on the next layout pass by itself.
    const sizer = scroller.lastElementChild;
    if (!sizer || sizer.getAttribute('aria-hidden') !== 'true') return;
    if (sizer === observedSizerRef.current) return;
    if (observedSizerRef.current) insetObserverRef.current.unobserve(observedSizerRef.current);
    insetObserverRef.current.observe(sizer, { box: 'border-box' });
    observedSizerRef.current = sizer;
  }, [applyHeaderInset]);

  // react-window delivers the scroller through its OWN state (verified in
  // the installed dist: the listRef handle's element is a getter over an
  // internal useState, re-invoked when it flips). At the mount commit that
  // state is still null — the real node arrives in a nested commit that
  // does NOT re-render this component — so the per-commit pass below alone
  // would never see it. Syncing here, at delivery, lands the correct inset
  // before first paint (React flushes that nested commit synchronously).
  const handleListRef = useCallback((api) => {
    listScrollerRef.current = api?.element ?? null;
    syncHeaderInset();
  }, [syncHeaderInset]);

  // Runs on EVERY commit (no dep array): anything that can flip the List's
  // vertical scrollbar — row count, dynamic row heights, the column gate,
  // the skeleton swapping the List in or out — also re-renders this
  // component, so this pass catches React-side changes as early as
  // possible. The List unmount detaches listScrollerRef to null during
  // that commit's mutation phase, before this effect runs, so the skeleton
  // case takes the explicit 0 branch above. It is NOT sufficient on its
  // own: in host Chrome the value written here still lagged the scrollbar
  // flip when a feed shrank below the viewport (the one-row feed kept a
  // stale 15px header, t_2bb1b175) — the sizer observation in
  // syncHeaderInset is the after-settlement re-measure that closes it.
  useLayoutEffect(() => {
    syncHeaderInset();
  });

  // Unmount-only teardown for the observer (the passes above re-attach by
  // identity; nothing in this mechanism writes state).
  useEffect(() => () => {
    if (insetObserverRef.current) insetObserverRef.current.disconnect();
  }, []);




  // Subcategory editing state
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [openSubcategoryEdit, setOpenSubcategoryEdit] = useState(false);
  const [openMerchantEdit, setOpenMerchantEdit] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [openFlagEdit, setOpenFlagEdit] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  // Account-ownership Phase 2: per-transaction source-of-funds tagging.
  const [openSourceEdit, setOpenSourceEdit] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  // Free-form user note per transaction.
  const [openNotesEdit, setOpenNotesEdit] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [editingDescription, setEditingDescription] = useState(null);
  const [showDeleteFlagConfirm, setShowDeleteFlagConfirm] = React.useState(false);

  // Bulk edit confirmation state
  const [bulkEditConfirm, setBulkEditConfirm] = useState({
    open: false,
    type: null,
    data: null
  });

  const theme = useTheme();

  const updateCategoryForTransactions = useUpdateCategoryForTransactions(mutateTransactions);
  const updateMerchantForTransactions = useUpdateMerchantForTransactions(mutateTransactions);
  const updateFlagForTransactions = useUpdateFlagForTransactions(mutateTransactions);
  const updateNotesForTransactions = useUpdateNotesForTransactions(mutateTransactions);
  const updateDescriptionForTransaction = useUpdateDescriptionForTransaction(mutateTransactions);

  // Manual transfer review (ADR-001): confirm a proposed transfer, or reject it
  // (durable NOT_TRANSFER suppression). Optimistic + revalidate via the same
  // enriched-transactions mutate the other edits use.
  const { openSnackbarError, openSnackbarSuccess } = useContext(SnackbarContext) || {};
  const transferReview = useTransferReviewActions(mutateTransactions);

  // Reviewer restore of a withheld flag (contextual flag assessment §4.6/§4.8):
  // one click on a "Withheld because" row turns the flag into a human verdict,
  // final. The mutation itself is optimistic (see useRestoreFlagForTransactions
  // in api/transactions.js). The hook returns a fresh closure every render, so
  // the latest one lives in a ref and handleRestoreFlag stays referentially
  // stable — unlike the sibling edit handlers, it crosses the memo boundary
  // into the virtual rows via itemData, and an unstable identity there would
  // re-render every row on every parent render.
  //
  // Undo of that restore (t_892abb39): a misclick is otherwise permanent (the
  // CONFIRMED verdict excludes the row from every future assessment), so the
  // confirmation names the inverse explicitly and the flag cell of a restored
  // row offers it too. Same ref discipline as the restore handler.
  const restoreFlag = useRestoreFlagForTransactions(mutateTransactions);
  const restoreFlagRef = useRef(null);
  restoreFlagRef.current = restoreFlag;
  const undoRestoreFlag = useUndoRestoreFlagForTransactions(mutateTransactions);
  const undoRestoreFlagRef = useRef(null);
  undoRestoreFlagRef.current = undoRestoreFlag;
  const canRestoreFlags = Boolean(mutateTransactions);

  // t_4553bc0c fix 1 — table-scoped single-shot guard for the flag actions.
  // While any flag restore/undo mutation is in flight, the handlers refuse
  // further flag actions and every flag action control renders inert
  // (flagActionPending rides into the rows through itemData). State, not
  // just a ref, because the rows must re-render to show it; the ref mirror
  // because handleRestoreFlag / handleUndoRestoreFlag are referentially
  // stable across itemData and must test the CURRENT value, not the one
  // their closure captured. That same handler check is also what covers the
  // post-restore confirmation's Undo: its element is created when the toast
  // opens and cannot re-render with later state. The guard clears in both
  // handlers' .finally — on success AND on failure — because a
  // stuck-disabled action row is a worse bug than the double-click.
  const [flagActionPending, setFlagActionPending] = useState(false);
  const flagActionPendingRef = useRef(false);
  const beginFlagAction = useCallback(() => {
    if (flagActionPendingRef.current) return false;
    flagActionPendingRef.current = true;
    setFlagActionPending(true);
    return true;
  }, []);
  const endFlagAction = useCallback(() => {
    flagActionPendingRef.current = false;
    setFlagActionPending(false);
  }, []);

  const handleUndoRestoreFlag = useCallback((event, transaction, { includeWithheld = false, rowSnapshot = null } = {}) => {
    event?.stopPropagation?.();
    if (!mutateTransactions) return;
    const txnId = transaction?.transaction?.id;
    const flagName = transaction?.flag?.name;
    if (!txnId || !flagName) return;
    if (!beginFlagAction()) return;
    undoRestoreFlagRef.current([txnId], { name: flagName }, { includeWithheld, rowSnapshot })
      .then(() => openSnackbarSuccess?.('Restore undone. The flag is withheld again.'))
      .catch(() => openSnackbarError?.('Could not undo the restore.'))
      .finally(endFlagAction);
  }, [mutateTransactions, openSnackbarSuccess, openSnackbarError, beginFlagAction, endFlagAction]);
  // The post-restore confirmation's action: the user is still in the withheld
  // view (that is the only place Restore lives), so the undo reads back in
  // the withheld shape and the row reappears beside its reason. The row is
  // named in the aria-label like the cell's own controls.
  //
  // t_eb5adf39 item 0: this action closes over the complete row as it stood
  // BEFORE the restore (flag, assessment, reason) — the same object the view
  // held at click time. Handing it to the mutation as rowSnapshot lets the
  // optimistic update write it back verbatim, so the re-entered row is whole
  // at t+0 instead of a bare half-height row until the server read lands.
  //
  // t_4a1821f3 (Alex: "Yes, let's see how that looks"): the treatment lives
  // in the shared FLAG_RESTORE_UNDO_PROPS above, spread at BOTH Undo call
  // sites — the filled primary-action surface, 40px target, row-naming
  // aria-label kept. The outlined white-fill version read as a chip; a toast
  // action and a cell action for one verb must not look like two controls.
  // t_4553bc0c: this element is created once, when the confirmation opens,
  // and cannot re-render with a later in-flight state — the guard that
  // covers it is the beginFlagAction() check inside handleUndoRestoreFlag
  // (a click while another flag action is in flight performs no action). At
  // its own creation the restore it belongs to has just settled, which is
  // why it is live here.
  const restoreUndoAction = (transaction) => (
    <Button
      {...FLAG_RESTORE_UNDO_PROPS}
      aria-label={`Undo restore of flag for ${[formatDate(transaction?.transaction?.posting_date), transaction?.merchant?.name].filter(Boolean).join(' ')}`}
      onClick={(e) => handleUndoRestoreFlag(e, transaction, { includeWithheld: true, rowSnapshot: transaction })}
    >
      Undo
    </Button>
  );
  const handleRestoreFlag = useCallback((event, transaction) => {
    event.stopPropagation();
    if (!mutateTransactions) return;
    const txnId = transaction?.transaction?.id;
    const flagName = transaction?.flag?.name;
    if (!txnId || !flagName) return;
    if (!beginFlagAction()) return;
    restoreFlagRef.current([txnId], {
      name: flagName,
      assessmentID: transaction.flag_assessment?.id || null,
    })
      .then(() => openSnackbarSuccess?.(
        // t_eb5adf39 item 5 (Alex): people do not read instructions in a
        // toast — the Undo is in it, visible; the message says only what
        // happened and what it means.
        'Flag restored. It now stands as a human flag, final.',
        restoreUndoAction(transaction)))
      .catch(() => openSnackbarError?.('Could not restore the flag.'))
      .finally(endFlagAction);
  }, [mutateTransactions, openSnackbarSuccess, openSnackbarError, handleUndoRestoreFlag, beginFlagAction, endFlagAction]);

  // All transfer verdicts happen inside the review dialog (after seeing the full
  // candidate set) — the grid row only opens it. Every change action is gated by
  // a confirmation dialog; the mutations are optimistic (see api/transfer_review).
  const [matchDialogAnchor, setMatchDialogAnchor] = useState(null);

  // Slim display legs for the optimistic re-match.
  const legFromAnchor = (a) => ({
    txn_id: a.transaction.id, posting_date: a.transaction.posting_date,
    description: a.transaction.description, amount: a.transaction.amount,
    account_display: a.bankAccount?._displayName, sub_account_number: a.bankAccount?.sub_account_number,
  });
  const legFromCandidate = (c) => ({
    txn_id: c.txn_id, posting_date: c.posting_date, description: c.description,
    amount: c.amount, account_display: c.account_display, sub_account_number: c.sub_account_number,
  });

  const { handleConfirmAction: confirmRejectTransfer, ConfirmDialogElement: RejectTransferDialog } = useConfirmAction(
    (txn) => {
      setMatchDialogAnchor(null);
      transferReview
        .reject(txn.transaction.id, txn.transfer_partner.transaction.id)
        .then(() => openSnackbarSuccess?.('Transfer link removed.'))
        .catch(() => openSnackbarError?.('Could not remove the transfer link.'));
    },
    {
      title: 'Remove transfer link',
      getEntityName: (txn) => txn?.transaction?.description || 'this transfer',
      confirmMessage: 'This unlinks the two transactions and stops the system from re-proposing this pair. You can restore it later from either transaction.',
      proceedButtonLabel: 'Remove link',
      destructive: true,
    }
  );

  const { handleConfirmAction: confirmConfirmTransfer, ConfirmDialogElement: ConfirmMatchDialog } = useConfirmAction(
    ({ anchorId, partnerId }) => {
      setMatchDialogAnchor(null);
      transferReview
        .confirm(anchorId, partnerId)
        .then(() => openSnackbarSuccess?.('Transfer confirmed.'))
        .catch(() => openSnackbarError?.('Could not confirm the transfer.'));
    },
    {
      title: 'Confirm transfer match',
      getEntityName: () => 'this transfer match',
      confirmMessage: 'Lock this as the confirmed match. The system will not change it automatically afterward.',
      proceedButtonLabel: 'Confirm match',
    }
  );

  const { handleConfirmAction: confirmLinkTransfer, ConfirmDialogElement: LinkMatchDialog } = useConfirmAction(
    ({ anchor, candidate }) => {
      setMatchDialogAnchor(null);
      transferReview
        .link(legFromAnchor(anchor), legFromCandidate(candidate))
        .then(() => openSnackbarSuccess?.('Transfer match updated.'))
        .catch(() => openSnackbarError?.('Could not update the transfer match.'));
    },
    {
      title: 'Change transfer match',
      getEntityName: ({ candidate } = {}) => candidate?.description || 'the selected candidate',
      confirmMessage: 'Re-point this transfer to the selected candidate. This replaces the current match and frees any prior partner.',
      proceedButtonLabel: 'Use this match',
    }
  );

  const handleReviewMatches = useCallback((event, transaction) => {
    event.stopPropagation();
    setMatchDialogAnchor(transaction);
  }, []);
  const handleSelectMatch = useCallback((candidate) => {
    if (matchDialogAnchor && candidate) confirmLinkTransfer({ anchor: matchDialogAnchor, candidate });
  }, [matchDialogAnchor, confirmLinkTransfer]);
  const handleDialogConfirm = useCallback((anchorId, partnerId) => {
    if (anchorId && partnerId) confirmConfirmTransfer({ anchorId, partnerId });
  }, [confirmConfirmTransfer]);
  const handleDialogReject = useCallback(() => {
    if (matchDialogAnchor?.transfer_partner) confirmRejectTransfer(matchDialogAnchor);
  }, [matchDialogAnchor, confirmRejectTransfer]);

  const getTransactionRowHeight = useCallback((index, rowProps) => {
    const normalHeight = 73; // Base height for a single row
    const transferHeight = normalHeight * 2 + 4; // Additional height for transfer partner row
    // console.log("Calculating row height for index:", index);
    // console.log("Data:", rowProps);

    const measuredHeight = measuredHeights.get(index);
    if (measuredHeight) {
      // console.log("Using measured height:", measuredHeight);
      return measuredHeight;
    }

    const {
      sortedTransactions,
      mapTransactionToListItem,
    } = rowProps.data;
    const transaction = sortedTransactions[index];
    const item = mapTransactionToListItem(transaction);
    return item.transferPartner ? transferHeight : normalHeight;
  }, [measuredHeights]);

  // Memoize sorted transactions
  const sortedTransactions = useMemo(() => {
    const _t0 = perfNow();
    const sorted = sortTransactions(transactions, sortKeys);
    // Diagnostic-only (gated, primitives only): sort cost + how often it recomputes.
    perfLog('view.sort', {
      rows: Array.isArray(transactions) ? transactions.length : 0,
      keys: sortKeys.length,
      ms: +(perfNow() - _t0).toFixed(1),
    });
    return sorted;
  }, [transactions, sortKeys]);

  // Surface the sort order to the parent so PDF print can render rows in
  // the same order the user is viewing. Memo above is referentially stable
  // unless transactions/sortKeys change (sortKeys only gets a new identity from
  // setSortKeys, never from a render), so this won't loop.
  useEffect(() => {
    if (typeof onSortedTransactionsChange === 'function') {
      onSortedTransactionsChange(sortedTransactions);
    }
  }, [sortedTransactions, onSortedTransactionsChange]);

  // Same idea for the sort order in words, so the printed report can state it.
  // Kept separate from the effect above because the description only depends on
  // the stack, not on the rows.
  const sortDescription = useMemo(() => describeSortKeys(sortKeys), [sortKeys]);

  useEffect(() => {
    if (typeof onSortDescriptionChange === 'function') {
      onSortDescriptionChange(sortDescription);
    }
  }, [sortDescription, onSortDescriptionChange]);

  // Get selected transactions
  const selectedTransactions = useMemo(() => {
    return sortedTransactions.filter(t => selectedTransactionIDs.has(t.transaction.id));
  }, [sortedTransactions, selectedTransactionIDs]);

  // Callback to handle row measurement updates

  const handleRowMeasured = useCallback((index, height) => {
    setMeasuredHeights((prev) => new Map(prev).set(index, height));
}, []);

// Add this useEffect to handle window resize
useEffect(() => {
  let resizeTimeout;

  const handleWindowResize = () => {
    // Clear previous timeout
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }

    // Debounce the resize handling.
    // react-window v2 has no resetAfterIndex — its ListImperativeAPI exposes
    // only `element` and `scrollToRow`. Heights are re-queried via the
    // rowHeight callback on every render, so clearing measuredHeights +
    // bumping measurementVersion (forces re-render) is sufficient to make
    // the List request fresh sizes for visible rows.
    resizeTimeout = setTimeout(() => {
      setMeasuredHeights(new Map());
      setMeasurementVersion(prev => prev + 1);
    }, 150); // Wait 150ms after resize stops
  };

  window.addEventListener('resize', handleWindowResize);

  return () => {
    window.removeEventListener('resize', handleWindowResize);
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
  };
}, []);

  // Clear measured heights when transactions change
  useEffect(() => {
    // The whole stack matters, not just the top key: promoting a tie-breaker or
    // flipping its direction reorders rows exactly like changing the primary key
    // does, and the height cache is index-keyed.
    const sortSignature = sortKeysSignature(sortKeys);
    const sortChanged = prevSortSignatureRef.current !== sortSignature;

    const countChanged =
      prevTransactionsRef.current.length !== sortedTransactions.length;

    // A row's height depends on whether it's a paired transfer (double height) or
    // single. Confirming/rejecting/re-matching a transfer flips a row between
    // single and paired WITHOUT changing the count — the index-keyed measured
    // height would otherwise stay stale and the paired row bleeds onto the next.
    // So also reset when the paired-vs-single pattern changes.
    const pairingSignature = sortedTransactions.map((t) => (t.transfer_partner ? '1' : '0')).join('');
    const pairingChanged = prevPairingSignatureRef.current !== pairingSignature;

    // Clearing the cache makes getTransactionRowHeight fall back to the correct
    // transferPartner-aware height until the ResizeObserver re-measures.
    if (sortChanged || countChanged || pairingChanged) {
      setMeasuredHeights(new Map());
      setMeasurementVersion(prev => prev + 1);
    }

    // Update refs
    prevTransactionsRef.current = sortedTransactions;
    prevSortSignatureRef.current = sortSignature;
    prevPairingSignatureRef.current = pairingSignature;
  }, [sortedTransactions, sortKeys]);

  // Merchant / Subcategory / Flag are optional columns. If one carrying a sort
  // key gets hidden, drop the key — an invisible tie-breaker silently reorders
  // both the table and the printed report with no control left to undo it.
  useEffect(() => {
    const columnVisible = {
      cardholderName: !!displayOptions?.showCardholder,
      merchantName: !!displayOptions?.showMerchant,
      subcategoryName: !!displayOptions?.showSubcategory,
      flagName: !!displayOptions?.showFlag,
    };

    setSortKeys((prev) => {
      const pruned = prev.filter((k) => columnVisible[k.field] !== false);
      // Same reference when nothing was pruned: displayOptions is often an inline
      // object, so this effect re-runs constantly and must not churn identity.
      if (pruned.length === prev.length) return prev;
      // Pruning everything would leave the table in raw load order, which is
      // arbitrary — bad for a view that feeds court exhibits. Fall back to the
      // defined default instead. (Emptying the stack from the panel IS allowed:
      // that is a deliberate act, and this effect never sees it.)
      return pruned.length ? pruned : DEFAULT_SORT_KEYS;
    });
  }, [displayOptions]);

  // Selection handlers
  const handleToggleSelection = useCallback((id) => {
    setSelectedTransactionIDs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTransactionIDs.size === sortedTransactions.length) {
      setSelectedTransactionIDs(new Set());
    } else {
      setSelectedTransactionIDs(new Set(sortedTransactions.map(t => t.transaction.id)));
    }
  }, [sortedTransactions, selectedTransactionIDs.size]);

  const handleClearSelection = useCallback(() => {
    setSelectedTransactionIDs(new Set());
  }, []);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleSort = useCallback((field) => {
    setSortKeys((prev) => nextSortKeys(prev, field));
  }, []);

  const handleResetSort = useCallback(() => {
    setSortKeys(DEFAULT_SORT_KEYS);
  }, []);

  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const handleToggleSortPanel = useCallback(() => setSortPanelOpen((prev) => !prev), []);

  // Only the columns actually on screen can be sort levels — a level on a
  // hidden column would reorder the table and the export with no visible cause.
  // Depends on the three BOOLEANS, not on displayOptions itself — callers pass
  // an inline object, so an object dep would hand the panel a fresh array on
  // every render.
  const showCardholder = !!displayOptions?.showCardholder;
  const showMerchant = !!displayOptions?.showMerchant;
  const showSubcategory = !!displayOptions?.showSubcategory;
  const showFlag = !!displayOptions?.showFlag;
  const sortableVisibleFields = useMemo(
    () => SORTABLE_FIELDS.filter((field) => {
      if (field === 'cardholderName') return showCardholder;
      if (field === 'merchantName') return showMerchant;
      if (field === 'subcategoryName') return showSubcategory;
      if (field === 'flagName') return showFlag;
      return true;
    }),
    [showCardholder, showMerchant, showSubcategory, showFlag],
  );

  // Props shared by every sortable column header. A header arrow is a
  // single-column control, so there is no precedence to announce — the stack is
  // stated in the sort-levels panel instead.
  const sortLabelProps = useCallback((field, columnLabel) => {
    const entry = sortKeys.find((k) => k.field === field);
    const direction = entry ? entry.direction : 'asc';
    const directionLabel = direction === 'asc' ? 'ascending' : 'descending';

    return {
      active: !!entry,
      direction,
      onClick: () => handleSort(field),
      'aria-label': entry
        ? `${columnLabel}, sorted ${directionLabel}. Activate to reverse, or use Sort Columns to sort by more than one.`
        : `Sort by ${columnLabel}`,
    };
  }, [sortKeys, handleSort]);

  // aria-sort belongs on exactly one column, so only the primary key gets it.
  const ariaSortFor = useCallback((field) => {
    if (sortKeys[0]?.field !== field) return undefined;
    return sortKeys[0].direction === 'asc' ? 'ascending' : 'descending';
  }, [sortKeys]);

  const handleClickOpen = useCallback((transaction) => {
    setSelectedTransaction(mapTransactionToDetailItem(transaction));
    setOpenDetail(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenDetail(false);
  }, []);

  // Modified edit handlers for bulk editing
  const handleEditSubcategory = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    // If we have selected transactions, use bulk edit mode
    if (selectedTransactionIDs.size > 0) {
      // Bulk edit mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk edit");
        return;
      }

      const editingSubcategory = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        currentCategoryType: relevant_transaction.category?.type,
        currentCategory: relevant_transaction.category?.name,
        currentSubcategory: relevant_transaction.subcategory?.name,
        currentRationale: relevant_transaction.category_rationale?.matched_basis,
        // Ledger segment (categorization-segments ADR-001): the current
        // subcategory's chart, and the account's default chart.
        currentSegment: relevant_transaction.subcategory?.segment,
        accountSegment: relevant_transaction.bankAccount?.ledger_segment
      };

      setEditingSubcategory(editingSubcategory);
      setOpenSubcategoryEdit(true);
    } else {
      // Single transaction edit mode
      if (!transaction) {
        console.error("No transaction provided for single edit");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single edit");
        return;
      }

      const editingSubcategory = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        currentCategoryType: relevant_transaction.category?.type,
        currentCategory: relevant_transaction.category?.name,
        currentSubcategory: relevant_transaction.subcategory?.name,
        currentRationale: relevant_transaction.category_rationale?.matched_basis,
        // Ledger segment (categorization-segments ADR-001): the current
        // subcategory's chart, and the account's default chart.
        currentSegment: relevant_transaction.subcategory?.segment,
        accountSegment: relevant_transaction.bankAccount?.ledger_segment
      };

      setEditingSubcategory(editingSubcategory);
      setOpenSubcategoryEdit(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  const handleEditMerchant = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    if (selectedTransactionIDs.size > 0) {
      // Bulk edit mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk edit");
        return;
      }

      const editingMerchant = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        merchantID: relevant_transaction.merchant?.id || null,
        merchantName: relevant_transaction.merchant?.name || null,
        merchantWebsite: relevant_transaction.merchant?.website || null,
        merchantPhone: relevant_transaction.merchant?.phone || null,
        merchantStreetAddress: relevant_transaction.merchant?.street_address || null,
        merchantCity: relevant_transaction.merchant?.city || null,
        merchantState: relevant_transaction.merchant?.state || null,
        merchantZip: relevant_transaction.merchant?.zip || null,
        merchantGeocode: relevant_transaction.merchant?.geocode || null,
      };

      setEditingMerchant(editingMerchant);
      setOpenMerchantEdit(true);
    } else {
      // Single transaction edit mode
      if (!transaction) {
        console.error("No transaction provided for single edit");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single edit");
        return;
      }

      const editingMerchant = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        merchantID: relevant_transaction.merchant?.id || null,
        merchantName: relevant_transaction.merchant?.name || null,
        merchantWebsite: relevant_transaction.merchant?.website || null,
        merchantPhone: relevant_transaction.merchant?.phone || null,
        merchantStreetAddress: relevant_transaction.merchant?.street_address || null,
        merchantCity: relevant_transaction.merchant?.city || null,
        merchantState: relevant_transaction.merchant?.state || null,
        merchantZip: relevant_transaction.merchant?.zip || null,
        merchantGeocode: relevant_transaction.merchant?.geocode || null,
      };

      setEditingMerchant(editingMerchant);
      setOpenMerchantEdit(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  const handleEditFlag = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    if (selectedTransactionIDs.size > 0) {
      // Bulk edit mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk edit");
        return;
      }

      const editingFlag = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setOpenFlagEdit(true);
    } else {
      // Single transaction edit mode
      if (!transaction) {
        console.error("No transaction provided for single edit");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single edit");
        return;
      }

      const editingFlag = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setOpenFlagEdit(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  const handleDeleteFlagClicked = useCallback((event, transaction, isTransferPartner) => {
    event.stopPropagation();

    if (selectedTransactionIDs.size > 0) {
      // Bulk delete mode
      if (selectedTransactions.length === 0) {
        console.error("No transactions in selectedTransactions array");
        return;
      }

      const firstTransaction = selectedTransactions[0];
      const relevant_transaction = isTransferPartner && firstTransaction.transfer_partner
        ? firstTransaction.transfer_partner
        : firstTransaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for bulk delete");
        return;
      }

      const editingFlag = {
        isBulkEdit: true,
        selectedCount: selectedTransactionIDs.size,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setShowDeleteFlagConfirm(true);
    } else {
      // Single transaction delete mode
      if (!transaction) {
        console.error("No transaction provided for single delete");
        return;
      }

      const relevant_transaction = isTransferPartner && transaction.transfer_partner
        ? transaction.transfer_partner
        : transaction;

      if (!relevant_transaction) {
        console.error("Could not find relevant transaction for single delete");
        return;
      }

      const editingFlag = {
        isBulkEdit: false,
        isTransferPartner: isTransferPartner,
        enrichedTransaction: relevant_transaction,
        currentFlag: relevant_transaction.flag?.name,
        currentRationale: relevant_transaction.flag_rationale?.matched_basis
      };

      setEditingFlag(editingFlag);
      setShowDeleteFlagConfirm(true);
    }
  }, [selectedTransactionIDs.size, selectedTransactions]);

  // Modified save handlers for bulk operations
  const handleSaveSubcategory = (newSubcategory) => {
    if (editingSubcategory?.isBulkEdit) {
      // Show confirmation dialog for bulk edit
      setBulkEditConfirm({
        open: true,
        type: 'category',
        data: newSubcategory
      });
    } else {
      // Single transaction edit
      const transactionIDList = [editingSubcategory.enrichedTransaction.transaction.id];
      const confidenceLevel = "Assigned by User";
      const predictionModel = "User Edit";
      updateCategoryForTransactions(transactionIDList, newSubcategory.type, newSubcategory.category, newSubcategory.subcategory, confidenceLevel, newSubcategory.rationale, predictionModel, newSubcategory.segment);
      handleCloseSubcategoryEdit();
    }
  };

  const handleSaveMerchant = (updatedMerchant) => {
    if (editingMerchant?.isBulkEdit) {
      setBulkEditConfirm({
        open: true,
        type: 'merchant',
        data: updatedMerchant
      });
    } else {
      console.log('Saving merchant:', {
        oldMerchant: editingMerchant,
        newMerchant: updatedMerchant
      });

      const transactionIDList = [editingMerchant.enrichedTransaction.transaction.id];
      const merchantData = buildMerchantPayload(updatedMerchant);

      updateMerchantForTransactions(transactionIDList, merchantData);
      handleCloseMerchantEdit();
    }
  };

  const handleSaveFlag = (updatedFlag) => {
    if (editingFlag?.isBulkEdit) {
      setBulkEditConfirm({
        open: true,
        type: 'flag',
        data: updatedFlag
      });
    } else {
      console.log('Saving flag:', {
        oldFlag: editingFlag,
        newFlag: updatedFlag
      });

      const transactionIDList = [editingFlag.enrichedTransaction.transaction.id];
      const flagData = {
        ...updatedFlag
      };

      updateFlagForTransactions(transactionIDList, flagData);
      handleCloseFlagEdit();
    }
  };

  const handleDeleteFlag = () => {
    if (editingFlag?.isBulkEdit) {
      const transactionIDList = Array.from(selectedTransactionIDs);
      const flagData = {};
      updateFlagForTransactions(transactionIDList, flagData);
      setShowDeleteFlagConfirm(false);
      setEditingFlag(null);
      handleClearSelection();
    } else {
      const transactionIDList = [editingFlag.enrichedTransaction.transaction.id];
      const flagData = {};
      updateFlagForTransactions(transactionIDList, flagData);
      setShowDeleteFlagConfirm(false);
      setEditingFlag(null);
    }
  };

  const handleDeleteFlagConfirm = () => {
    handleDeleteFlag();
  };

  const handleDeleteFlagCancel = () => {
    setShowDeleteFlagConfirm(false);
    setEditingFlag(null);
  };

  // Bulk edit confirmation handler
  const handleBulkEditConfirm = () => {
    const transactionIDList = Array.from(selectedTransactionIDs);

    switch (bulkEditConfirm.type) {
      case 'category':
        const confidenceLevel = "Assigned by User";
        const predictionModel = "User Edit";
        updateCategoryForTransactions(
          transactionIDList,
          bulkEditConfirm.data.type,
          bulkEditConfirm.data.category,
          bulkEditConfirm.data.subcategory,
          confidenceLevel,
          bulkEditConfirm.data.rationale,
          predictionModel,
          bulkEditConfirm.data.segment
        );
        handleCloseSubcategoryEdit();
        break;

      case 'merchant':
        const merchantData = buildMerchantPayload(bulkEditConfirm.data);
        updateMerchantForTransactions(transactionIDList, merchantData);
        handleCloseMerchantEdit();
        break;

      case 'flag':
        const flagData = { ...bulkEditConfirm.data };
        updateFlagForTransactions(transactionIDList, flagData);
        handleCloseFlagEdit();
        break;
    }

    setBulkEditConfirm({ open: false, type: null, data: null });
    handleClearSelection();
  };

  const handleCloseSubcategoryEdit = () => {
    setOpenSubcategoryEdit(false);
    setEditingSubcategory(null);
  };

  const handleCloseMerchantEdit = () => {
    setOpenMerchantEdit(false);
    setEditingMerchant(null);
  };

  const handleCloseFlagEdit = () => {
    setOpenFlagEdit(false);
    setEditingFlag(null);
  };

  // Open the source-of-funds editor for one transaction (the main row or a
  // transfer-partner row passes the relevant enriched transaction). Single-
  // transaction only — source is per-transaction, not bulk.
  const handleEditSource = useCallback((event, txnObj) => {
    event.stopPropagation();
    const t = txnObj?.transaction;
    if (!t?.id) return;
    setEditingSource({ transactionId: t.id, amount: t.amount });
    setOpenSourceEdit(true);
  }, []);

  const handleCloseSourceEdit = () => {
    setOpenSourceEdit(false);
    setEditingSource(null);
  };

  // Open the note editor for one transaction (main row or transfer-partner row).
  // Single-transaction only — a note is per-transaction, not bulk.
  const handleEditNotes = useCallback((event, txnObj) => {
    event.stopPropagation();
    const t = txnObj?.transaction;
    if (!t?.id) return;
    setEditingNotes({ transactionId: t.id, notes: t.notes || '' });
    setOpenNotesEdit(true);
  }, []);

  const handleCloseNotesEdit = () => {
    setOpenNotesEdit(false);
    setEditingNotes(null);
  };

  const handleSaveNotes = (notes) => {
    if (!editingNotes?.transactionId) return;
    updateNotesForTransactions([editingNotes.transactionId], notes);
    handleCloseNotesEdit();
  };

  // Correct an OCR-misread description (main row or transfer-partner row).
  // Single-transaction only — a description is row-specific.
  const handleEditDescription = useCallback((event, txnObj) => {
    event.stopPropagation();
    const t = txnObj?.transaction;
    if (!t?.id) return;
    setEditingDescription({
      transactionId: t.id,
      description: t.description || '',
      originalDescription: t.description_edited != null ? (t.description_original ?? '') : null,
    });
  }, []);

  const handleCloseDescriptionEdit = () => setEditingDescription(null);

  const handleSaveDescription = (description) => {
    if (!editingDescription?.transactionId) return;
    const reverting = !description;
    Promise.resolve(updateDescriptionForTransaction(editingDescription.transactionId, description))
      .then(() => openSnackbarSuccess?.(reverting
        ? 'Description reverted to the scanned text.'
        : 'Description updated. Categorization will refresh shortly.'))
      .catch(() => openSnackbarError?.('Could not update the description.'));
    handleCloseDescriptionEdit();
  };

  // Memoize subcategory cell render function
  const renderSubcategoryCell = useCallback((item, transaction, isTransferPartner = false) => {
    if (!displayOptions?.showSubcategory) return null;

    return (
      <TableCell sx={{ width: '150px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<CategoryIcon />}
            label={item.subcategoryName}
            size="small"
            variant="outlined"
            sx={{ opacity: isTransferPartner ? 0.8 : 1 }}
          />
          {userProfile?.isCaseContributor() &&
          <IconButton
            size="small"
            onClick={(e) => handleEditSubcategory(e, transaction, isTransferPartner)}
            sx={{
              opacity: 0.5,
              '&:hover': { opacity: 1 },
              transition: 'opacity 0.2s'
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
  }
        </Box>
      </TableCell>
    );
  }, [displayOptions, handleEditSubcategory, userProfile]);

  // Data to pass to virtual rows
  const itemData = useMemo(() => ({
    sortedTransactions,
    theme,
    displayOptions,
    handleClickOpen,
    handleEditSubcategory,
    handleEditMerchant,
    handleEditFlag,
    handleEditSource,
    handleEditNotes,
    handleEditDescription,
    handleDeleteFlagClicked,
    handleRestoreFlag,
    handleUndoRestoreFlag,
    canRestoreFlags,
    flagActionPending,
    handleReviewMatches,
    mapTransactionToListItem,
    formatDate,
    formatCurrency,
    renderSubcategoryCell,
    onRowMeasured: handleRowMeasured,
    measurementVersion,
    selectedTransactionIDs,
    handleToggleSelection
  }), [
    sortedTransactions,
    theme,
    displayOptions,
    handleClickOpen,
    handleEditSubcategory,
    handleEditMerchant,
    handleEditFlag,
    handleEditSource,
    handleEditNotes,
    handleEditDescription,
    handleDeleteFlagClicked,
    handleReviewMatches,
    renderSubcategoryCell,
    handleRowMeasured,
    mapTransactionToListItem,
    formatDate,
    formatCurrency,
    measurementVersion,
    handleRestoreFlag,
    handleUndoRestoreFlag,
    canRestoreFlags,
    flagActionPending,
    selectedTransactionIDs,
    handleToggleSelection
  ]);

  const isAllSelected = selectedTransactionIDs.size === sortedTransactions.length && sortedTransactions.length > 0;
  const isIndeterminate = selectedTransactionIDs.size > 0 && selectedTransactionIDs.size < sortedTransactions.length;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Selection Toolbar */}
      {selectedTransactionIDs.size > 0 && (
        <Paper
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderRadius: 1,
            mb: 1
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {selectedTransactionIDs.size} transaction{selectedTransactionIDs.size !== 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {displayOptions?.showSubcategory && userProfile?.isCaseContributor() && (
              <Tooltip title="Edit category for selected">
                <Button
                  size="small"
                  startIcon={<CategoryIcon />}
                  onClick={(e) => handleEditSubcategory(e, null, false)}
                  variant="outlined"
                >
                  Edit Category
                </Button>
              </Tooltip>
            )}
            {displayOptions?.showMerchant && userProfile?.isCaseContributor() && (
              <Tooltip title="Edit merchant for selected">
                <Button
                  size="small"
                  startIcon={<PlaceIcon />}
                  onClick={(e) => handleEditMerchant(e, null, false)}
                  variant="outlined"
                >
                  Edit Merchant
                </Button>
              </Tooltip>
            )}
            {displayOptions?.showFlag && userProfile?.isCaseContributor() && (
              <Tooltip title="Edit flag for selected">
                <Button
                  size="small"
                  startIcon={<WarningIcon />}
                  onClick={(e) => handleEditFlag(e, null, false)}
                  variant="outlined"
                >
                  Edit Flag
                </Button>
              </Tooltip>
            )}
            {displayOptions?.showFlag && userProfile?.isCaseContributor() && (
              <Tooltip title="Remove flags from selected">
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={(e) => handleDeleteFlagClicked(e, null, false)}
                  variant="outlined"
                  color="error"
                >
                  Remove Flags
                </Button>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={handleClearSelection}>
            Clear Selection
          </Button>
        </Paper>
      )}

      {/* Summary Section */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          mt: 1
        }}
      >
        {/* Stats row. The sort-levels panel's collapsed summary sits at its
            right; the panel's EXPANDED editor drops below, full bar width. */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Total Transactions
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="60px" height="48px" />
          ) : (
            <Typography variant="h4" fontWeight="bold">
              {formatInteger(sortedTransactions.length)}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Total Amount
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="120px" height="48px" />
          ) : (
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                color: sortedTransactions.reduce((sum, t) => sum + (t.transaction?.amount || 0), 0) < 0
                  ? theme.palette.error.main
                  : theme.palette.success.main,
              }}
            >
              {formatCurrency(sortedTransactions.reduce((sum, t) => sum + (t.transaction?.amount || 0), 0))}
            </Typography>
          )}
        </Box>

        {/* The feedback line stays visible whether the editor is open or shut,
            so the row order is always accounted for — same wording captioned on
            the printed PDF and the Excel Report Info sheet. */}
        <SortLevelsSummary
          sortKeys={sortKeys}
          open={sortPanelOpen}
          onToggleOpen={handleToggleSortPanel}
        />
        </Box>

        {/* Multi-column sorting lives here, NOT on the column headers. */}
        {sortPanelOpen && (
          <SortLevelsEditor
            sortKeys={sortKeys}
            onChange={setSortKeys}
            onReset={handleResetSort}
            visibleFields={sortableVisibleFields}
            isDefault={isDefaultSortKeys(sortKeys)}
          />
        )}
      </Paper>

      {/* Virtual Scrolling Table */}
      <Paper
        sx={{
          mt: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Table Header - Fixed */}
        {/* The header lives in its own clipped scrollport, inset on the
            right by the List's MEASURED vertical-scrollbar width: the
            pinned Flag header cell then shares one right edge with the
            pinned body cells at every scroll offset on classic-scrollbar
            platforms, and on every other platform the measured value is 0
            and nothing moves. The inset arrives as the --flag-header-inset
            custom property written imperatively on this node by the
            listRef block in TransactionViewerDisplay — never through
            React state, whose layout feedback looped the page
            (t_c6346a75). */}
        <div
          ref={headerInsetHostRef}
          data-header-inset-host
          style={{ flexShrink: 0, overflowX: 'hidden', overflowY: 'auto', marginRight: 'var(--flag-header-inset, 0)' }}
        >
        <Table sx={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'separate' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: '48px' }}>
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: '100px' }} aria-sort={ariaSortFor('transactionDate')}>
                <TableSortLabel {...sortLabelProps('transactionDate', 'Date')} sx={{ fontWeight: 'bold' }}>
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '120px' }} aria-sort={ariaSortFor('bankAccountName')}>
                <TableSortLabel {...sortLabelProps('bankAccountName', 'Account')} sx={{ fontWeight: 'bold' }}>
                  Account
                </TableSortLabel>
              </TableCell>
              {displayOptions?.showCardholder && (
                <TableCell sx={{ width: '150px' }} aria-sort={ariaSortFor('cardholderName')}>
                  <TableSortLabel {...sortLabelProps('cardholderName', 'Cardholder')} sx={{ fontWeight: 'bold' }}>
                    Cardholder
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sx={{ width: '300px' }} aria-sort={ariaSortFor('description')}>
                <TableSortLabel {...sortLabelProps('description', 'Description')} sx={{ fontWeight: 'bold' }}>
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '100px' }} aria-sort={ariaSortFor('amount')}>
                <TableSortLabel {...sortLabelProps('amount', 'Amount')} sx={{ fontWeight: 'bold' }}>
                  Amount
                </TableSortLabel>
              </TableCell>
              {displayOptions?.showMerchant && (
                <TableCell sx={{ width: '200px' }} aria-sort={ariaSortFor('merchantName')}>
                  <TableSortLabel {...sortLabelProps('merchantName', 'Merchant')} sx={{ fontWeight: 'bold' }}>
                    Merchant
                  </TableSortLabel>
                </TableCell>
              )}
              {displayOptions?.showSubcategory && (
                <TableCell sx={{ width: '150px' }} aria-sort={ariaSortFor('subcategoryName')}>
                  <TableSortLabel {...sortLabelProps('subcategoryName', 'Subcategory')} sx={{ fontWeight: 'bold' }}>
                    Subcategory
                  </TableSortLabel>
                </TableCell>
              )}
              {displayOptions?.showSource && (
                <TableCell sx={{ width: '120px', fontWeight: 'bold' }}>
                  Source
                </TableCell>
              )}
              {displayOptions?.showNotes && (
                <TableCell sx={{ width: '200px', fontWeight: 'bold' }}>
                  Notes
                </TableCell>
              )}
              {displayOptions?.showFlag && (
                <TableCell
                  data-flag-pinned
                  sx={{ width: FLAG_COLUMN_WIDTH, ...FLAG_PIN_POSITION }}
                  aria-sort={ariaSortFor('flagName')}
                >
                  <TableSortLabel {...sortLabelProps('flagName', 'Flag')} sx={{ fontWeight: 'bold' }}>
                    Flag
                  </TableSortLabel>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
        </Table>
        </div>

        {/* Virtual List */}
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton key={index} variant="rectangular" height="73px" sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <List
              rowComponent={VirtualTransactionRow}
              rowCount={sortedTransactions.length}
              rowHeight={getTransactionRowHeight}
              rowProps={{ data: itemData }}
              overscanCount={5}
              listRef={handleListRef}
              style={{ scrollbarGutter: 'stable' }}
            >
            </List>
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <TransactionDetailDialog
        open={openDetail}
        selectedTransaction={selectedTransaction}
        handleClose={handleClose}
      />

      <CategoryEditDialog
        open={openSubcategoryEdit}
        onClose={handleCloseSubcategoryEdit}
        currentCategoryType={editingSubcategory?.currentCategoryType}
        currentCategory={editingSubcategory?.currentCategory}
        currentSubcategory={editingSubcategory?.currentSubcategory}
        currentRationale={editingSubcategory?.currentRationale}
        currentSegment={editingSubcategory?.currentSegment}
        accountSegment={editingSubcategory?.accountSegment}
        onSave={handleSaveSubcategory}
        isBulkEdit={editingSubcategory?.isBulkEdit}
        selectedCount={editingSubcategory?.selectedCount}
      />

      <MerchantEditDialog
        open={openMerchantEdit}
        onClose={handleCloseMerchantEdit}
        onSave={handleSaveMerchant}
        merchant={editingMerchant}
        isBulkEdit={editingMerchant?.isBulkEdit}
        selectedCount={editingMerchant?.selectedCount}
      />

      <FlagEditDialog
        open={openFlagEdit}
        onClose={handleCloseFlagEdit}
        currentFlag={editingFlag?.currentFlag}
        currentRationale={editingFlag?.currentRationale}
        onSave={handleSaveFlag}
        onDelete={handleDeleteFlag}
        isBulkEdit={editingFlag?.isBulkEdit}
        selectedCount={editingFlag?.selectedCount}
      />

      <SourceEditDialog
        open={openSourceEdit}
        onClose={handleCloseSourceEdit}
        onSaved={mutateTransactions}
        transactionId={editingSource?.transactionId}
        amount={editingSource?.amount}
      />

      <NotesEditDialog
        open={openNotesEdit}
        onClose={handleCloseNotesEdit}
        onSave={handleSaveNotes}
        notes={editingNotes?.notes}
      />

      <DescriptionEditDialog
        open={!!editingDescription}
        onClose={handleCloseDescriptionEdit}
        onSave={handleSaveDescription}
        description={editingDescription?.description}
        originalDescription={editingDescription?.originalDescription}
      />

      {/* Manual transfer review: confirmation dialogs for every change action. */}
      <RejectTransferDialog />
      <ConfirmMatchDialog />
      <LinkMatchDialog />

      {/* Manual transfer review: review the candidate set and re-point the match. */}
      <TransferMatchDialog
        open={Boolean(matchDialogAnchor)}
        onClose={() => setMatchDialogAnchor(null)}
        anchorTransaction={matchDialogAnchor}
        currentPartnerId={matchDialogAnchor?.transfer_partner?.transaction?.id || null}
        currentDisposition={matchDialogAnchor?.transfer_partner?.disposition || null}
        onSelectMatch={handleSelectMatch}
        onConfirm={handleDialogConfirm}
        onReject={handleDialogReject}
      />

      <BulkEditConfirmDialog
        open={bulkEditConfirm.open}
        onClose={() => setBulkEditConfirm({ open: false, type: null, data: null })}
        onConfirm={handleBulkEditConfirm}
        editType={bulkEditConfirm.type}
        editData={bulkEditConfirm.data}
        selectedTransactions={selectedTransactions}
      />

      <Dialog
        open={showDeleteFlagConfirm}
        onClose={handleDeleteFlagCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {editingFlag?.isBulkEdit ? `flags from ${editingFlag.selectedCount} transactions` : 'this flag'}? This action cannot be undone.
          </Typography>
          {editingFlag?.currentFlag && !editingFlag?.isBulkEdit && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Flag: <strong>{editingFlag?.currentFlag}</strong>
              </Typography>
              {editingFlag?.currentRationale && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Rationale: {editingFlag?.currentRationale}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteFlagCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteFlagConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionViewerDisplay;